import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import axios from 'axios';
import { swalConfirm } from './utils/swalHelpers';
import { 
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Link
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import PublicVisaPortalPage from './pages/PublicVisaPortalPage';

const SettingsTab = lazy(() => import('./tabs/SettingsTab'));
const MarketSettingsTab = lazy(() => import('./tabs/MarketSettingsTab'));
const NotificationDashboardTab = lazy(() => import('./tabs/NotificationDashboardTab'));
const EmailRulesTab = lazy(() => import('./tabs/EmailRulesTab'));
const MediaSettingsTab = lazy(() => import('./tabs/MediaSettingsTab'));
const AuditLogTab = lazy(() => import('./tabs/AuditLogTab'));
const BookingsTab = lazy(() => import('./tabs/BookingsTab'));
const CostingsTab = lazy(() => import('./tabs/CostingsTab'));
const CustomersTab = lazy(() => import('./tabs/CustomersTab'));
const CustomerAnalyticsDashboard = lazy(() => import('./tabs/CustomerAnalyticsDashboard'));
const InboxTab = lazy(() => import('./tabs/InboxTab'));
const ToursTab = lazy(() => import('./tabs/ToursTab'));
const DeparturesTab = lazy(() => import('./tabs/DeparturesTab'));
const RemindersTab = lazy(() => import('./tabs/RemindersTab'));
const HotelsTab = lazy(() => import('./tabs/HotelsTab'));
const RestaurantsTab = lazy(() => import('./tabs/RestaurantsTab'));
const TransportsTab = lazy(() => import('./tabs/TransportsTab'));
const VisasTab = lazy(() => import('./tabs/VisasTab'));
const VisaTemplatesTab = lazy(() => import('./tabs/VisaTemplatesTab'));
const TicketsTab = lazy(() => import('./tabs/TicketsTab'));
const AirlinesTab = lazy(() => import('./tabs/AirlinesTab'));
const LandtoursTab = lazy(() => import('./tabs/LandtoursTab'));
const InternalDocsTab = lazy(() => import('./tabs/InternalDocsTab'));
const LicensesTab = lazy(() => import('./tabs/LicensesTab'));
const BURulesTab = lazy(() => import('./tabs/BURulesTab'));
const InsurancesTab = lazy(() => import('./tabs/InsurancesTab'));
const VisaProvidersTab = lazy(() => import('./tabs/VisaProvidersTab'));
const OpToursTab = lazy(() => import('./tabs/OpToursTab'));
const TravelSupportTab = lazy(() => import('./tabs/TravelSupportTab'));

const DispatchScheduleTab = lazy(() => import('./tabs/DispatchScheduleTab'));
const MessageTemplatesTab = lazy(() => import('./tabs/MessageTemplatesTab'));
const ZaloChatDrawer = lazy(() => import('./components/drawers/ZaloChatDrawer'));
import usePushNotifications from './hooks/usePushNotifications';
const LeaveRequestsTab = lazy(() => import('./tabs/LeaveRequestsTab'));
const RoomBookingTab = lazy(() => import('./tabs/RoomBookingTab'));
const PaymentVouchersTab = lazy(() => import('./tabs/PaymentVouchersTab'));
// ═══ Tour Đoàn Tab Imports ═══
const GroupProjectsTab = lazy(() => import('./tabs/GroupProjectsTab'));
const MiceLeadsTab = lazy(() => import('./tabs/MiceLeadsTab'));
const GroupLeadersTab = lazy(() => import('./tabs/GroupLeadersTab'));
const B2BCompaniesTab = lazy(() => import('./tabs/B2BCompaniesTab'));
const DashboardTab = lazy(() => import('./tabs/DashboardTab'));
const WorkspaceTab = lazy(() => import('./tabs/workspace/WorkspaceTab'));
const ManagementDashboardTab = lazy(() => import('./tabs/ManagementDashboardTab'));
const CEODepartureDashboardTab = lazy(() => import('./tabs/CEODepartureDashboardTab'));
const GroupDashboardTab = lazy(() => import('./tabs/GroupDashboardTab'));
const LeadsTab = lazy(() => import('./tabs/LeadsTab'));
const LeadsDashboardTab = lazy(() => import('./tabs/LeadsDashboardTab'));
const StaffPerformanceTab = lazy(() => import('./tabs/StaffPerformanceTab'));
const GuidesTab = lazy(() => import('./tabs/GuidesTab'));
const MarketingAdsTab = lazy(() => import('./tabs/MarketingAdsTab'));
const MarketingBudgetPlanTab = lazy(() => import('./tabs/MarketingBudgetPlanTab'));
const GoogleAdsTab = lazy(() => import('./tabs/GoogleAdsTab'));
const UsersTab = lazy(() => import('./tabs/UsersTab'));
import StaffCalendarView from './components/StaffCalendarView';
const TeamsTab = lazy(() => import('./tabs/TeamsTab'));
const ManualTab = lazy(() => import('./tabs/ManualTab'));
const MyProfileTab = lazy(() => import('./tabs/MyProfileTab'));
const TeamDirectoryTab = lazy(() => import('./tabs/TeamDirectoryTab'));
const ZaloSandboxTab = lazy(() => import('./tabs/ZaloSandboxTab'));
const ZaloAISettingsTab = lazy(() => import('./tabs/ZaloAISettingsTab'));
import AddLeadModal from './components/modals/AddLeadModal';
import EditLeadModal from './components/modals/EditLeadModal';
import NotificationBell from './components/common/NotificationBell';
import CommandPalette from './components/CommandPalette';
import SystemAnnouncementPopup from './components/modals/SystemAnnouncementPopup';
const EmailGroupsTab = lazy(() => import('./tabs/EmailGroupsTab'));
import LeaveRequestModal from './components/modals/LeaveRequestModal';
import { AddCustomerModal, EditCustomerModal } from './components/modals/CustomerModals';
import { AddBookingModal } from './components/modals/BookingModals';
import { AddUserModal, EditUserModal, ChangePasswordModal } from './components/modals/UserModals';
import { RolePermissionModal, UserPermissionOverrideModal } from './components/modals/PermissionModals';
import LeadNotesModal from './components/modals/LeadNotesModal';
import { AddTemplateModal, AddDepartureModal, EditTemplateModal, EditDepartureModal } from './components/modals/TourModals';
import GuideModal from './components/modals/GuideModal';
import ViewDeparturePage from './pages/ViewDeparturePage';
import AgencySharePage from './pages/AgencySharePage';
import ServiceContractViewer from './pages/ServiceContractViewer';
import VisaFormTemplatesPage from './pages/VisaFormTemplatesPage';
import DocumentsPage from './pages/DocumentsPage';
import RagDocsManager from './components/Knowledge/RagDocsManager';
const GlobalChatTab = lazy(() => import('./tabs/GlobalChatTab'));
import BrandGuidelinePage from './pages/BrandGuidelinePage';
import BrandLogoPage from './pages/BrandLogoPage';
import BrandColorPage from './pages/BrandColorPage';
import BrandTypographyPage from './pages/BrandTypographyPage';
import BrandSocialPage from './pages/BrandSocialPage';
import BrandLayout from './pages/BrandLayout';
import AdminTripDashboard from './pages/AdminTripDashboard';
import PublicReceiptPage from './pages/PublicReceiptPage';
import PublicAnnouncementPage from './pages/PublicAnnouncementPage';
import ZnsDemoTab from './tabs/ZnsDemoTab';
import HuongDanTaoNghiPhepPage from './pages/HuongDanTaoNghiPhepPage';
import HuongDanDatPhongHopPage from './pages/HuongDanDatPhongHopPage';
import HuongDanErpHub from './pages/HuongDanErpHub';

import { 
  Menu,
  X,
  Home,
  Users, 
  Map, 
  LayoutDashboard, 
  MessageSquare, 
  Calendar, 
  CalendarClock,
  CalendarDays,
  CheckCircle,
  TrendingUp,
  UserPlus,
  LogOut,
  Sun,
  Moon,
  Plus,
  Edit3,
  Edit2,
  Trash2,
  Filter,
  Clock,
  ArrowUpRight,
  MoreVertical,
  MoreHorizontal,
  Search,
  PlusCircle,
  Send,
  Package,
  ShoppingCart,
  UserCheck,
  Settings,
  Image as ImageIcon,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Eye,
  EyeOff,
  User,
  Lock,
  PieChart,
  FileText,
  DollarSign,
  BookOpen,
  Target,
  Shield,
  Briefcase,
  Building,
  Activity,
  MapPin,
  ScanText,
  Cpu,
  Mail,
  MessageCircle,
  Bot,
  Sparkles
} from 'lucide-react';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataDeletion from './pages/DataDeletion';
const OrgChartTab = lazy(() => import('./tabs/OrgChartTab'));
const CskhBoardTab = lazy(() => import('./tabs/CskhBoardTab'));
const CskhTodoTab = lazy(() => import('./tabs/CskhTodoTab'));
const CskhSearchTab = lazy(() => import('./tabs/CskhSearchTab'));
const CskhRulesTab = lazy(() => import('./tabs/CskhRulesTab'));
const CustomerReviewsTab = lazy(() => import('./tabs/CustomerReviewsTab'));
const WorkflowTab = lazy(() => import('./tabs/WorkflowTab'));
import PassportBulkScanner from './components/tools/PassportBulkScanner';

const addToastGlobal = (message, setToasts, type = 'success') => {
  const id = Date.now();
  // Nếu có chữ "lỗi" hoặc "error" tự xem như type = 'error' để giữ lâu hơn
  const isError = type === 'error' || message.toLowerCase().includes('lỗi');
  setToasts(prev => [...prev, { id, message, type: isError ? 'error' : type }]);
  
  if (!isError) {
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000); // 5s cho thành công
  } else {
    // Thông báo lỗi cho hiển thị 15 giây hoặc đến khi user tự tắt (sẽ thêm nút X)
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 15000);
  }
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  const { requestSubscription, isSubscribing } = usePushNotifications(localStorage.getItem('token'));
  const VALID_TABS = ['workspace', 'visa-products', 'dashboard', 'management-dashboard', 'ceo-departures-dashboard', 'leads', 'leads-dashboard', 'zalo-sandbox', 'zalo-ai-settings', 'marketing-ads', 'marketing-budget-plan', 'marketing-google-ads', 'staff-performance', 'inbox', 'message-templates', 'tours', 'departures', 'guides', 'bookings', 'customers', 'settings', 'market-settings', 'media-settings', 'users', 'staff-calendar', 'teams', 'bus', 'costings', 'manual', 'hotels', 'restaurants', 'transports', 'visas', 'tickets', 'airlines', 'insurances', 'licenses', 'announcements', 'bu-rules', 'op-tours', 'vouchers', 'travel-support', 'leaves', 'meeting-rooms', 'group-dashboard', 'group-mice-leads', 'group-projects', 'group-leaders', 'b2b-companies', 'accountants', 'team-directory', 'org-chart', 'workflow', 'my-profile', 'audit-logs', 'passport-ocr', 'reminders', 'landtours', 'companies', 'cskh-board', 'cskh-todo', 'cskh-search', 'cskh-rules', 'payment-vouchers', 'agent-manager', 'tai-lieu', 'email-groups', 'notification-dashboard', 'email-rules', 'visa-providers', 'visa-form-templates', 'dispatch-schedule', 'notification-center', 'zns-demo'];

  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname.substring(1);
    if (path.startsWith('guides')) return 'guides';
    if (path.startsWith('manual')) return 'manual';
    if (path === 'group/companies') return 'b2b-companies';
    if (path.startsWith('group/')) return path.replace('/', '-');
    
    let defaultTab = 'notification-center';
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        // Cập nhật QA Guard: Chặn tài khoản không có quyền vào Notification Center mặc định
        const perms = u.permissions || {};
        if (u.role !== 'admin' && (!perms.leads || !perms.leads.can_view)) {
          defaultTab = 'workspace';
        }
      } catch(e) {}
    }
    
    const basePath = path.split('/')[0];
    if (VALID_TABS.includes(path)) return path;
    if (VALID_TABS.includes(basePath)) return basePath;
    return defaultTab;
  });


  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleToken = urlParams.get('token');
    const googleUserStr = urlParams.get('user');
    if (googleToken && googleUserStr) {
        localStorage.setItem('token', googleToken);
        localStorage.setItem('user', decodeURIComponent(googleUserStr));
        return true;
    }
    return !!(localStorage.getItem('token') && localStorage.getItem('user'));
  });
  const [inboxPsid, setInboxPsid] = useState(null);
  
  // Global Inbox Drawer State
  const [isInboxDrawerOpen, setIsInboxDrawerOpen] = useState(false);
  const [drawerInboxPsid, setDrawerInboxPsid] = useState(null);

  const handleOpenInboxDrawer = (psid) => {
      setDrawerInboxPsid(psid);
      setIsInboxDrawerOpen(true);
  };

  // Global Zalo Drawer State
  const [isZaloDrawerOpen, setIsZaloDrawerOpen] = useState(false);
  const [drawerZaloUid, setDrawerZaloUid] = useState(null);

  const handleOpenZaloDrawer = (zaloUid) => {
      setDrawerZaloUid(zaloUid);
      setIsZaloDrawerOpen(true);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isInboxDrawerOpen) setIsInboxDrawerOpen(false);
        if (isZaloDrawerOpen) setIsZaloDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInboxDrawerOpen, isZaloDrawerOpen]);

  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    try { return savedUser ? JSON.parse(savedUser) : null; } catch(e) { return null; }
  });

  const handleUpdateGlobalUser = (updatedFields) => {
    setUser(prev => {
      if (!prev) return prev;
      const newUser = { ...prev, ...updatedFields };
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
  };

  // Mobile Smart Scroll Header: Hide on scroll down, show on scroll up (kéo lên)
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY || document.documentElement.scrollTop;
      
      // Near top of page or iOS rubber-banding: always show
      if (currentScrollY <= 40) {
        setIsHeaderHidden(false);
        lastScrollYRef.current = Math.max(0, currentScrollY);
        return;
      }

      const diff = currentScrollY - lastScrollYRef.current;

      // Scrolling DOWN with noticeable motion -> hide header
      if (diff > 8 && currentScrollY > 60) {
        setIsHeaderHidden(true);
      } 
      // Scrolling UP ("kéo lên") -> show header
      else if (diff < -8) {
        setIsHeaderHidden(false);
      }

      lastScrollYRef.current = currentScrollY;
    };

    const handleCustomScroll = (e) => {
      const { diff, scrollTop } = e.detail || {};
      if (scrollTop <= 40) {
        setIsHeaderHidden(false);
      } else if (diff > 8) {
        setIsHeaderHidden(true);
      } else if (diff < -8) {
        setIsHeaderHidden(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('app-scroll-direction', handleCustomScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('app-scroll-direction', handleCustomScroll);
    };
  }, []);

  useEffect(() => {
    setIsHeaderHidden(false);
  }, [activeTab]);

  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [tours, setTours] = useState([]);
  const [tourTemplates, setTourTemplates] = useState([]);
  const [tourDepartures, setTourDepartures] = useState([]);
  const [guides, setGuides] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [metaToken, setMetaToken] = useState('');
  const [roles, setRoles] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRolePermModal, setShowRolePermModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [userToEditPerms, setUserToEditPerms] = useState(null);
  const [editingUserAccount, setEditingUserAccount] = useState(null);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [showChangeMyPassword, setShowChangeMyPassword] = useState(false);
  const [metaSettings, setMetaSettings] = useState({
    meta_app_id: '',
    meta_app_secret: '',
    meta_verify_token: '',
    meta_page_access_token: '',
    meta_page_id: ''
  });
  const [leadFilters, setLeadFilters] = useState({ status: '', source: '', search: '', bu_group: '', assigned_to: '', timeRange: 'today', startDate: '', endDate: '', tours: [], hasPhone: '', dateField: 'created_at' });
  const [tourFilters, setTourFilters] = useState({ search: '', tour_type: '', destination: '', has_departure: '', status: '', guide_id: '', timeRange: 'all', startDate: '', endDate: '' });
  const [bookingFilters, setBookingFilters] = useState({ search: '', status: '', bookingStatus: '', paymentStatus: '' });
  const [bookingCurrentPage, setBookingCurrentPage] = useState(1);
  const [bookingTotalPages, setBookingTotalPages] = useState(1);
  const [customerFilters, setCustomerFilters] = useState({ search: '' });
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [newLead, setNewLead] = useState({ 
    name: '', phone: '', email: '', gender: '', birth_date: '', 
    source: 'Messenger', tour_id: '', consultation_note: '', 
    bu_group: '', assigned_to: '', classification: 'Mới',
    last_contacted_at: ''
  });
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState(null);
  const [leadNotes, setLeadNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newCustomerNote, setNewCustomerNote] = useState('');
  const [hoveredNote, setHoveredNote] = useState({ id: null, content: '', count: 0, date: null, x: 0, y: 0 });
  
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '', phone: '', email: '', gender: '', birth_date: '', 
    nationality: 'Việt Nam', id_card: '', id_expiry: '', address: '', 
    preferred_contact: 'Zalo', role: 'booker', customer_segment: 'New Customer',
    tour_interests: '', special_requests: '', internal_notes: '',
    location_city: '', travel_season: '', created_at: new Date().toLocaleDateString('en-CA')
  });

  // Sidebar Submenu State
  const [isToursMenuOpen, setIsToursMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const menuTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const handleOpenMobileMenu = () => setIsMobileMenuOpen(true);
    window.addEventListener('open-mobile-menu', handleOpenMobileMenu);
    return () => window.removeEventListener('open-mobile-menu', handleOpenMobileMenu);
  }, []);

  // Auto-sync profile on load to ensure Avatar and Name are always fresh
  useEffect(() => {
    if (isLoggedIn) {
      axios.get('/api/users/me', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(res => {
          if (res.data) {
            setUser(prev => {
              const newUser = { ...prev, ...res.data };
              localStorage.setItem('user', JSON.stringify(newUser));
              return newUser;
            });
          }
        })
        .catch(err => console.error("Auto profile sync failed", err));
    }
  }, [isLoggedIn]);

  const sidebarRef = useRef(null);

  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showAddDepartureModal, setShowAddDepartureModal] = useState(false);
  const [showEditDepartureModal, setShowEditDepartureModal] = useState(false);
  const [editingDeparture, setEditingDeparture] = useState(null);
  const [viewingDeparture, setViewingDeparture] = useState(null);
  const [showViewDepartureModal, setShowViewDepartureModal] = useState(false);

  const handleViewDeparture = (dep) => {
    const depId = dep.id || dep.tour_departure_id;
    navigate(`/departures/view/${depId}`);
  };

  const handleViewBookingsForDeparture = (depCode) => {
    setBookingFilters({ search: depCode, bookingStatus: '', paymentStatus: '' });
    setActiveTab('bookings');
    navigate('/bookings');
  };

  const handleOpenCustomer = async (customerId) => {
    let cust = customers.find(c => c.id === customerId);
    if (!cust) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/customers/${customerId}`, { headers: { Authorization: `Bearer ${token}` } });
        cust = res.data;
      } catch (err) {
        console.error("Lỗi khi tải thông tin Khách hàng:", err);
        return;
      }
    }
    if (cust) {
      setEditingCustomer(cust);
    }
  };

  const [showAddGuideModal, setShowAddGuideModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [newTemplate, setNewTemplate] = useState({
    name: '', destination: '', duration: '', tour_type: 'Standard', tags: '',
    itinerary: '', highlights: '', inclusions: '', exclusions: '',
    base_price: 0, internal_cost: 0, expected_margin: 0
  });

  const [newDeparture, setNewDeparture] = useState({
    tour_template_id: '', start_date: '', end_date: '', max_participants: 20, status: 'Mở bán',
    actual_price: 0, discount_price: 0,
    guide_id: '', operator_id: '', min_participants: 10, break_even_pax: 12,
    price_rules: [
      { id: Math.random().toString(36).substring(7), name: 'Người lớn', price: 0, is_default: true },
      { id: Math.random().toString(36).substring(7), name: 'Trẻ em (06-11 tuổi)', price: 0, is_default: false },
      { id: Math.random().toString(36).substring(7), name: 'Trẻ em (< 02 tuổi)', price: 0, is_default: false }
    ],
    additional_services: [],
    notes: ''
  });

  const [newGuide, setNewGuide] = useState({
    name: '', phone: '', email: '', languages: '', rating: 5, status: 'Available'
  });
  const [editingGuide, setEditingGuide] = useState(null);

  // Gantt Chart / Timeline State
  const [guideActiveTab, setGuideActiveTab] = useState(() => {
    const p = window.location.pathname.substring(1);
    if (p === 'guides/timeline') return 'timeline';
    if (p === 'guides/dashboard') return 'dashboard';
    if (p === 'guides/reviews') return 'reviews';
    return 'list';
  });
  const [guideTimelineData, setGuideTimelineData] = useState([]);
  const [guideStats, setGuideStats] = useState(null);
  const [guideTimeFilter, setGuideTimeFilter] = useState({
    type: 'month',
    date: new Date(),
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 30))
  });
  const [guideFilters, setGuideFilters] = useState({ search: '', status: '', language: '' });
  const [customerActiveTab, setCustomerActiveTab] = useState(() => {
    return localStorage.getItem('customerActiveTab') || 'list';
  });
  
  useEffect(() => {
    localStorage.setItem('customerActiveTab', customerActiveTab);
  }, [customerActiveTab]);

  const [groupLeaderActiveTab, setGroupLeaderActiveTab] = useState(() => {
    return localStorage.getItem('groupLeaderActiveTab') || 'list';
  });
  
  useEffect(() => {
    localStorage.setItem('groupLeaderActiveTab', groupLeaderActiveTab);
  }, [groupLeaderActiveTab]);

  const [b2bActiveTab, setB2bActiveTab] = useState(() => {
    return localStorage.getItem('b2bActiveTab') || 'list';
  });
  
  useEffect(() => {
    localStorage.setItem('b2bActiveTab', b2bActiveTab);
  }, [b2bActiveTab]);

  const [bus, setBus] = useState([]);


  const handleQuickUpdate = async (leadId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { [field]: value };
      
      // If setting status to 'Chốt đơn', also set won_at if not present
      if (field === 'status' && value === 'Chốt đơn') {
        const lead = leads.find(l => l.id === leadId);
        if (lead && !lead.won_at) {
          payload.won_at = new Date();
        }
      }

      await axios.put(`/api/leads/${leadId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      addToastGlobal('Đã cập nhật thay đổi thành công.', setToasts);
    } catch (err) {
      addToastGlobal('Lỗi khi cập nhật nhanh: ' + err.message, setToasts);
    }
  };
  const [isLightMode, setIsLightMode] = useState(true);
  const [editingLead, setEditingLead] = useState(null);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [leadToConvert, setLeadToConvert] = useState(null); // Fix popup close
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [tourToDelete, setTourToDelete] = useState(null);
  const [departureToDelete, setDepartureToDelete] = useState(null);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [transportToDelete, setTransportToDelete] = useState(null);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [airlineToDelete, setAirlineToDelete] = useState(null);
  const [landtourToDelete, setLandtourToDelete] = useState(null);
  const [insuranceToDelete, setInsuranceToDelete] = useState(null);
  const [b2bCompanyToDelete, setB2bCompanyToDelete] = useState(null);
  const [groupLeaderToDelete, setGroupLeaderToDelete] = useState(null);
  const [groupProjectToDelete, setGroupProjectToDelete] = useState(null);
  const [visaProviderToDelete, setVisaProviderToDelete] = useState(null);
  const [bookingToEdit, setBookingToEdit] = useState(null);

  const LEAD_SOURCES = ['Messenger', 'Zalo', 'Tiktok', 'Khách giới thiệu', 'Hotline', 'Khác'];
  const LEAD_STATUSES = ['Mới', 'Đang liên hệ', 'Liên hệ lần 2', 'Chốt đơn', 'Thất bại', 'Không phản hồi'];
  const LEAD_CLASSIFICATIONS = ['Mới', 'Tiềm Năng', 'Tiềm Năng Cao', 'Không có nhu cầu'];
  const CUSTOMER_ROLES = ['Người đại diện (booker)', 'Khách đi kèm'];
  const CUSTOMER_SEGMENTS = ['New Customer', 'Repeat Customer', 'VIP 3', 'VIP 2', 'VIP 1'];
  const CONTACT_METHODS = ['Zalo', 'Call', 'Email'];
  const CITY_OPTIONS = ['TP. Hồ Chí Minh', 'Hà Nội', 'Đà Nẵng', 'Khác'];

  const addToast = (msg, type) => addToastGlobal(msg, setToasts, type);

  useEffect(() => {
    const fullPath = location.pathname.substring(1);
    // /tai-lieu/* is a standalone page — skip tab sync entirely
    if (fullPath.startsWith('tai-lieu')) return;
    if (!fullPath) {
      let defaultTab = 'notification-center';
      if (user && user.role !== 'admin') {
        const perms = user.permissions || {};
        if (!perms.leads || !perms.leads.can_view) {
          defaultTab = 'workspace';
        }
      }
      setActiveTab(defaultTab);
      return;
    }
    if (fullPath.startsWith('guides')) { 
      setActiveTab('guides'); 
      if (fullPath === 'guides/reviews') setGuideActiveTab('reviews');
      else if (fullPath === 'guides/dashboard') setGuideActiveTab('dashboard');
      else if (fullPath === 'guides/timeline') setGuideActiveTab('timeline');
      else setGuideActiveTab('list');
      return; 
    }
    if (fullPath.startsWith('customers')) {
      setActiveTab('customers');
      if (fullPath === 'customers/analytics' || fullPath === 'customers/dashboard') setCustomerActiveTab('analytics');
      else if (fullPath === 'customers/cskh-rules') setCustomerActiveTab('cskh-rules');
      else if (fullPath === 'customers/cskh-board') setCustomerActiveTab('cskh-board');
      else if (fullPath === 'customers/cskh-todo') setCustomerActiveTab('cskh-todo');
      else if (fullPath === 'customers/cskh-search') setCustomerActiveTab('cskh-search');
      else if (fullPath === 'customers/calendar') setCustomerActiveTab('calendar');
      else setCustomerActiveTab('list');
      return;
    }
    if (fullPath.startsWith('manual')) { setActiveTab('manual'); return; }
    if (fullPath === 'email/mailboxes') { setActiveTab('email-mailboxes'); return; }
    if (fullPath.startsWith('group/')) { 
      if (fullPath === 'group/companies') {
        setActiveTab('b2b-companies');
        if (location.search.includes('tab=calendar')) {
          setB2bActiveTab('calendar');
        } else {
          setB2bActiveTab('list');
        }
      } else if (fullPath === 'group/google-ads') {
        setActiveTab('marketing-google-ads');
      } else {
        setActiveTab(fullPath.replace('/', '-'));
      }
      return; 
    }
    
    const basePath = fullPath.split('/')[0];

    // Intercept deep links for Lead notifications
    if (fullPath.startsWith('leads/') || fullPath === 'workspace') {
       let leadId = null;
       if (fullPath.startsWith('leads/')) {
           leadId = fullPath.split('/')[1];
       } else if (fullPath === 'workspace') {
           const searchParams = new URLSearchParams(location.search);
           leadId = searchParams.get('lead_id');
       }

       if (leadId) {
           const token = localStorage.getItem('token');
           if (token) {
               axios.get(`/api/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } })
                   .then(res => {
                       const lead = res.data;
                       if (lead && lead.facebook_psid) {
                           // Route to Inbox instead of Lead modal
                           navigate(`/inbox?psid=${lead.facebook_psid}`, { replace: true });
                       } else {
                           // Open modal as fallback if no facebook_psid
                           setTimeout(() => {
                               window.dispatchEvent(new CustomEvent('open-edit-lead-modal-from-chat', { detail: leadId }));
                           }, 100);
                       }
                   })
                   .catch(err => console.error('Failed to fetch lead info', err));
           }
       }
    }

    if (VALID_TABS.includes(fullPath)) {
       setActiveTab(fullPath);
       if (fullPath === 'inbox') {
           const searchParams = new URLSearchParams(location.search);
           const psid = searchParams.get('psid');
           if (psid) setInboxPsid(psid);
       }
    } else if (VALID_TABS.includes(basePath)) {
       setActiveTab(basePath);
    }

    setIsMobileMenuOpen(false);
    setHoveredMenu(null);
  }, [location.pathname, location.search, user]);

  // Click / touch outside to close hovered profile menu or flyouts
  useEffect(() => {
    if (!hoveredMenu) return;
    const handleGlobalClick = (e) => {
      // Do not unmount on mousedown if clicking inside submenu items or sidebar navigation items
      if (e.target.closest('.submenu-flyout') || e.target.closest('.nav-item')) {
        return;
      }
      if (hoveredMenu === 'user-profile') {
        if (!e.target.closest('.user-profile') && !e.target.closest('.user-profile-dropdown')) {
          setHoveredMenu(null);
        }
      } else {
        setHoveredMenu(null);
      }
    };
    document.addEventListener('mousedown', handleGlobalClick);
    document.addEventListener('touchstart', handleGlobalClick);
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
      document.removeEventListener('touchstart', handleGlobalClick);
    };
  }, [hoveredMenu]);

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        if (token && config.url !== '/api/auth/login') {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase()) && !config.headers['Idempotency-Key'] && !config.headers['idempotency-key']) {
          config.headers['Idempotency-Key'] = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `idemp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    const resInterceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Do not auto logout on 403 (Permission Denied). Only logout on 401 (Unauthorized/Token Expired)
        if (error.response && [401].includes(error.response.status)) {
          if (error.config && error.config.url !== '/api/auth/login') {
            console.error('Logout triggered by 401 on URL:', error.config.url);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsLoggedIn(false);
            setUser(null);
            navigate('/login');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [navigate]);

  useEffect(() => {
    if (isLightMode) {
      document.body.classList.add('light-mode');
    } else {
      document.body.classList.remove('light-mode');
    }
  }, [isLightMode]);

  // Global listener for navigating to company detail from Calendar
  useEffect(() => {
    const handleSwitch = (e) => {
      sessionStorage.setItem('pendingCompanyOpen', e.detail);
      navigate('/group/companies');
      setActiveTab('b2b-companies');
      setB2bActiveTab('list');
    };
    const handleSwitchProject = (e) => {
      sessionStorage.setItem('pendingProjectOpen', e.detail);
      navigate('/group/projects');
      setActiveTab('group-projects');
    };
    const handleJumpToDeparture = (e) => {
      sessionStorage.setItem('pendingOpenDepartureId', e.detail);
      navigate('/op-tours');
      setActiveTab('op-tours');
    };
    window.addEventListener('switchAndOpenCompany', handleSwitch);
    window.addEventListener('switchAndOpenProject', handleSwitchProject);
    window.addEventListener('jumpToDeparture', handleJumpToDeparture);

    // Command Palette Quick Actions — navigate to module pages
    const handleOpenAddLead = () => { navigate('/leads'); setActiveTab('leads'); };
    const handleOpenAddCustomer = () => { navigate('/customers'); setActiveTab('customers'); };
    const handleOpenAddBooking = () => { navigate('/bookings'); setActiveTab('bookings'); };

    const handleOpenEditLeadFromChat = async (e) => {
      const leadId = e.detail;
      try {
        const res = await axios.get(`/api/leads/${leadId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        setEditingLead(res.data);
      } catch (err) {
        console.error("Failed to fetch lead info for modal", err);
      }
    };

    window.addEventListener('open-add-lead-modal', handleOpenAddLead);
    window.addEventListener('open-add-customer-modal', handleOpenAddCustomer);
    window.addEventListener('open-add-booking-modal', handleOpenAddBooking);
    window.addEventListener('open-edit-lead-modal-from-chat', handleOpenEditLeadFromChat);

    return () => {
      window.removeEventListener('switchAndOpenCompany', handleSwitch);
      window.removeEventListener('switchAndOpenProject', handleSwitchProject);
      window.removeEventListener('jumpToDeparture', handleJumpToDeparture);
      window.removeEventListener('open-add-lead-modal', handleOpenAddLead);
      window.removeEventListener('open-add-customer-modal', handleOpenAddCustomer);
      window.removeEventListener('open-add-booking-modal', handleOpenAddBooking);
      window.removeEventListener('open-edit-lead-modal-from-chat', handleOpenEditLeadFromChat);
    };

  }, [navigate]);



  useEffect(() => {
    // Handle Google OAuth Redirect params
    const urlParams = new URLSearchParams(window.location.search);
    const googleToken = urlParams.get('token');
    const googleUserStr = urlParams.get('user');
    const googleError = urlParams.get('error');
    const toastMsg = urlParams.get('toast');

    if (toastMsg === 'google_sync_success') {
        addToastGlobal('Đã đồng bộ tài khoản Google thành công!', setToasts, 'success');
        window.history.replaceState({}, document.title, window.location.pathname);
    } else if (toastMsg === 'google_sync_failed') {
        addToastGlobal('Đồng bộ tài khoản Google thất bại.', setToasts, 'error');
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (googleError) {
        let msg = 'Đăng nhập Google thất bại.';
        if (googleError === 'account_not_found') msg = 'Email này chưa được đăng ký trong hệ thống CRM.';
        if (googleError === 'account_disabled') msg = 'Tài khoản của bạn đã bị vô hiệu hóa.';
        setError(msg);
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (googleToken && googleUserStr) {
        window.history.replaceState({}, document.title, window.location.pathname);
    }
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      try {
        const u = JSON.parse(savedUser);
        if (u) {
          setUser(u);
          setIsLoggedIn(true);

          // Silent token refresh — get latest role from DB without re-login
          axios.get('/api/auth/refresh', { headers: { Authorization: `Bearer ${savedToken}` } })
            .then(res => {
              localStorage.setItem('token', res.data.token);
              localStorage.setItem('user', JSON.stringify(res.data.user));
              setUser(res.data.user);
            })
            .catch(() => {}); // Fail silently — old token still works
        }
      } catch (err) {
        console.error("Session restore failed", err);
      }
    }
  }, []);
  
  // Clean up forms/modals when switching tabs
  useEffect(() => {
    setEditingLead(null);
    setShowAddLeadModal(false);
    setSelectedLeadForNotes(null);
    setEditingCustomer(null);
    setShowAddCustomerModal(false);
    setEditingGuide(null);
    setShowAddGuideModal(false);
  }, [activeTab]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchLeads();
      fetchTourTemplates();
      fetchTourDepartures();
      fetchGuides();
      fetchBookings();
      fetchCustomers();
      fetchUsers();
      fetchSettings();
      fetchBUs();
      fetchMyPermissions();
    }
  }, [isLoggedIn]);

  const fetchMyPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/permissions/my', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPermissions(res.data.permissions);
    } catch (err) { console.error('Fetch permissions failed', err); }
  };

  const fetchBUs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/business-units', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBus(res.data);
    } catch (err) { console.error('Error fetching BUs:', err); }
  };

  const handleUpdateBU = async (id, payload) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/business-units/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBUs();
    } catch (err) { console.error('Error updating BU:', err); }
  };

  const handleReorderBUs = async (orders) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/business-units/reorder', { orders }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Cập nhật vị trí thành công!');
      fetchBUs();
    } catch (err) { addToast('Lỗi khi sắp xếp BU'); }
  };

  const handleCreateBU = async (payload) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/business-units', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Tạo BU mới thành công!');
      fetchBUs();
    } catch (err) { addToast('Lỗi khi tạo BU'); }
  };

  const fetchTourTemplates = async () => {
    try {
      const response = await axios.get('/api/tours', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTourTemplates(response.data.filter(t => !String(t.name || '').includes('[Tour Cũ]')));
    } catch (err) { console.error(err); }
  };

  const fetchTourDepartures = async () => {
    try {
      const response = await axios.get('/api/departures', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTourDepartures(response.data);
    } catch (err) { console.error(err); }
  };

  const fetchGuides = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/guides', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuides(response.data);
    } catch (err) { console.error(err); }
  };

  const fetchGuideTimeline = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/guides/timeline/data', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuideTimelineData(response.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchGuideStats = useCallback(async (start, end) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/guides/stats', {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: start, endDate: end }
      });
      setGuideStats(response.data);
    } catch (err) { 
      console.error(err); 
      setGuideStats({ error: err.response?.data?.message || err.message });
      addToast(err.response?.data?.message || err.message, 'error');
    }
  }, []);

  const handleOpenAddGuide = () => {
    setEditingGuide(null);
    setNewGuide({
      name: '', phone: '', email: '', languages: '', rating: 5, status: 'Active',
      experience: 0, bio: '', specialties: '', avatar_url: '', dob: '', address: '',
      gender: '', passport: '', passport_expiry: '', id_card: '', id_card_date: '',
      guide_card_type: '', guide_card_number: '', guide_card_expiry: '', passport_url: '', guide_card_url: ''
    });
    setShowAddGuideModal(true);
  };

  const handleEditGuide = (guide) => {
    setEditingGuide(guide);
    setNewGuide({
      ...guide
    });
    setShowAddGuideModal(true);
  };

  const handleUpdateGuide = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/guides/${editingGuide.id}`, newGuide, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGuides();
      setShowAddGuideModal(false);
      setEditingGuide(null);
      setNewGuide({
        name: '', phone: '', email: '', languages: '', rating: 5, status: 'Available'
      });
      addToast('Đã cập nhật thông tin HDV thành công!');
    } catch (err) { addToast('Lỗi khi cập nhật HDV: ' + err.message); }
  };

  const handleDeleteGuide = async (id) => {
    if (!await swalConfirm('Bạn có chắc chắn muốn xoá hướng dẫn viên này?', { title: 'Xác nhận xóa HDV' })) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/guides/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGuides();
      addToast('Đã xoá hướng dẫn viên!');
    } catch (err) {
      // 409 = HDV đang gắn với tour, hỏi lại lần nữa
      if (err.response && err.response.status === 409 && err.response.data.has_deps) {
        if (await swalConfirm(`⚠️ ${err.response.data.message}\n\nBạn vẫn muốn xóa?`, { title: 'Cảnh báo', icon: 'warning' })) {
          try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/guides/${id}?force=true`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            fetchGuides();
            addToast('Đã xoá hướng dẫn viên!');
          } catch (err2) { addToast('Lỗi khi xoá: ' + (err2.response?.data?.message || err2.message)); }
        }
      } else {
        addToast('Lỗi khi xoá: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/tours', newTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTourTemplates();
      setShowAddTemplateModal(false);
      setNewTemplate({
        name: '', destination: '', duration: '', tour_type: 'Group Tour', tags: '',
        itinerary: '', highlights: '', inclusions: '', exclusions: '',
        base_price: 0, internal_cost: 0, expected_margin: 0
      });
      addToast('Đã thêm sản phẩm tour mới thành công!');
    } catch (err) { addToast('Lỗi khi thêm tour: ' + err.message); }
  };

  const handleUpdateTemplate = async (updatedData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/tours/${updatedData.id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTourTemplates();
      setEditingTemplate(null);
      addToast('Đã cập nhật sản phẩm tour thành công!');
    } catch (err) { addToast('Lỗi khi cập nhật tour: ' + err.message); }
  };

  const handleAddDeparture = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/departures', newDeparture, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTourDepartures();
      setShowAddDepartureModal(false);
      setNewDeparture({
        tour_template_id: '', start_date: '', end_date: '', max_participants: 20, status: 'Mở bán',
        actual_price: 0, discount_price: 0,
        guide_id: '', operator_id: '', min_participants: 10, break_even_pax: 12,
        price_rules: [
          { id: Math.random().toString(36).substring(7), name: 'Người lớn', price: 0, is_default: true },
          { id: Math.random().toString(36).substring(7), name: 'Trẻ em (06-11 tuổi)', price: 0, is_default: false },
          { id: Math.random().toString(36).substring(7), name: 'Trẻ em (< 02 tuổi)', price: 0, is_default: false }
        ],
        additional_services: [],
        notes: ''
      });
      addToast('Đã lên lịch khởi hành mới thành công!');
    } catch (err) { addToast('Lỗi khi thêm lịch khởi hành: ' + (err.response?.data?.message || err.message)); }
  };

  const handleEditDeparture = (dep) => {
    setEditingDeparture(dep);
    setShowEditDepartureModal(true);
  };

  const handleUpdateDeparture = async (updatedData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/departures/${updatedData.id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTourDepartures();
      setShowEditDepartureModal(false);
      setEditingDeparture(null);
      addToast('Đã cập nhật lịch khởi hành thành công!');
      return null;
    } catch (err) { 
      const errorStr = err.response?.data?.message || err.message;
      addToast('Lỗi khi cập nhật lịch khởi hành: ' + errorStr); 
      return errorStr;
    }
  };

  const handleDeleteDeparture = (id) => {
    setDepartureToDelete(id);
  };

  const handleEditBooking = (booking) => {
    setBookingToEdit(booking);
    setShowAddBookingModal(true);
  };

  const handleDeleteBooking = (id) => {
    setBookingToDelete(id);
  };

  const confirmDeleteBooking = async () => {
    if (!bookingToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/bookings/${bookingToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBookings();
      addToast('Đã đưa đơn hàng vào Thùng rác (Nhật ký hệ thống).');
      setBookingToDelete(null);
    } catch (err) {
      if (err.response && err.response.status === 409 && err.response.data.has_transactions) {
        if (await swalConfirm(`⚠️ ${err.response.data.message}\n\nBạn vẫn muốn tiếp tục đưa vào thùng rác chứ? (Khuyên dùng: Hủy phiếu thu trước)`, { title: 'Cảnh báo', icon: 'warning' })) {
          try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/bookings/${bookingToDelete}?force=true`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            fetchBookings();
            addToast('Đã đưa đơn hàng vào Thùng rác.');
          } catch (err2) { addToast('Lỗi khi xoá: ' + (err2.response?.data?.message || err2.message)); }
        }
        setBookingToDelete(null);
      } else {
        addToast('Lỗi khi xoá: ' + (err.response?.data?.message || err.message)); 
        setBookingToDelete(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteHotel = async () => {
    if (!hotelToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Luôn gọi force=true vì user đã xác nhận ở modal "XÓA THỰC SỰ"
      await axios.delete(`/api/hotels/${hotelToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa khách sạn thành công.');
      setHotelToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadHotels'));
    } catch (err) {
      addToast('Lỗi khi xóa khách sạn: ' + (err.response?.data?.message || err.message), 'error');
      setHotelToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteRestaurant = async () => {
    if (!restaurantToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Luôn gọi force=true vì user đã xác nhận ở modal "XÓA THỰC SỰ"
      await axios.delete(`/api/restaurants/${restaurantToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa nhà hàng thành công.');
      setRestaurantToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadRestaurants'));
    } catch (err) {
      addToast('Lỗi khi xóa nhà hàng: ' + (err.response?.data?.message || err.message), 'error');
      setRestaurantToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteTransport = async () => {
    if (!transportToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/transports/${transportToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa nhà xe thành công.');
      setTransportToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadTransports'));
    } catch (err) {
      addToast('Lỗi khi xóa nhà xe: ' + (err.response?.data?.message || err.message), 'error');
      setTransportToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteTicket = async () => {
    if (!ticketToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/tickets/${ticketToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa vé tham quan thành công.');
      setTicketToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadTickets'));
    } catch (err) {
      addToast('Lỗi khi xóa vé: ' + (err.response?.data?.message || err.message), 'error');
      setTicketToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteAirline = async () => {
    if (!airlineToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/airlines/${airlineToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa hãng hàng không thành công.');
      setAirlineToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadAirlines'));
    } catch (err) {
      addToast('Lỗi khi xóa: ' + (err.response?.data?.message || err.message), 'error');
      setAirlineToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteLandtour = async () => {
    if (!landtourToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/landtours/${landtourToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa Land Tour thành công.');
      setLandtourToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadLandtours'));
    } catch (err) {
      addToast('Lỗi khi xóa: ' + (err.response?.data?.message || err.message), 'error');
      setLandtourToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteInsurance = async () => {
    if (!insuranceToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/insurances/${insuranceToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa Bảo Hiểm thành công.');
      setInsuranceToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadInsurances'));
    } catch (err) {
      addToast('Lỗi khi xóa: ' + (err.response?.data?.message || err.message), 'error');
      setInsuranceToDelete(null);
    } finally {
      setLoading(false);
    }
  };



  const confirmDeleteVisaProvider = async () => {
    if (!visaProviderToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/visa-providers/${visaProviderToDelete}?force=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa Nhà Cung Cấp Visa thành công.');
      setVisaProviderToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadVisaProviders'));
    } catch (err) {
      addToast('Lỗi khi xóa: ' + (err.response?.data?.message || err.message), 'error');
      setVisaProviderToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteB2bCompany = async () => {
    if (!b2bCompanyToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/b2b-companies/${b2bCompanyToDelete}`, { headers: { Authorization: `Bearer ${token}` } });
      addToast('Đã xóa Doanh nghiệp thành công.');
      setB2bCompanyToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadB2bCompanies'));
    } catch (err) {
      addToast('Lỗi: ' + (err.response?.data?.message || err.message), 'error');
      setB2bCompanyToDelete(null);
    } finally { setLoading(false); }
  };

  const confirmDeleteGroupLeader = async () => {
    if (!groupLeaderToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/group-leaders/${groupLeaderToDelete}`, { headers: { Authorization: `Bearer ${token}` } });
      addToast('Đã xóa Trưởng đoàn thành công.');
      setGroupLeaderToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadGroupLeaders'));
    } catch (err) {
      addToast('Lỗi: ' + (err.response?.data?.message || err.message), 'error');
      setGroupLeaderToDelete(null);
    } finally { setLoading(false); }
  };

  const confirmDeleteGroupProject = async () => {
    if (!groupProjectToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/group-projects/${groupProjectToDelete}`, { headers: { Authorization: `Bearer ${token}` } });
      addToast('Đã xóa Dự án Tour thành công.');
      setGroupProjectToDelete(null);
      window.dispatchEvent(new CustomEvent('reloadGroupProjects'));
    } catch (err) {
      addToast('Lỗi: ' + (err.response?.data?.message || err.message), 'error');
      setGroupProjectToDelete(null);
    } finally { setLoading(false); }
  };

  const confirmDeleteDeparture = async () => {
    if (!departureToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/departures/${departureToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTourDepartures();
      addToast('Đã xoá lịch khởi hành.');
      setDepartureToDelete(null);
    } catch (err) { 
      addToast('Lỗi khi xoá: ' + (err.response?.data?.message || err.message)); 
      setDepartureToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateDeparture = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/departures/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTourDepartures();
      addToast('Đã nhân bản lịch khởi hành thành công!');
    } catch (err) { addToast('Lỗi khi nhân bản: ' + (err.response?.data?.message || err.message)); }
  };

  const handleAddGuide = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/guides', newGuide, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGuides();
      setShowAddGuideModal(false);
      setNewGuide({
        name: '', phone: '', email: '', languages: '', rating: 5, status: 'Available'
      });
      addToast('Đã thêm hướng dẫn viên mới thành công!');
    } catch (err) { addToast('Lỗi khi thêm HDV: ' + err.message); }
  };

  const getSourceIcon = (source) => {
    const s = (source || '').toLowerCase();
    if (s.includes('messenger')) return <MessageSquare size={14} color="#0084ff" />;
    if (s.includes('zalo')) return <MessageSquare size={14} color="#0068ff" />;
    if (s.includes('hotline')) return <Clock size={14} color="#22c55e" />;
    if (s.includes('giới thiệu')) return <UserCheck size={14} color="#ec4899" />;
    return <TrendingUp size={14} color="#6366f1" />;
  };

  useEffect(() => {
    if (editingLead) {
      fetchNotes(editingLead.id);
    }
  }, [editingLead]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetaSettings(res.data);
      if (res.data.meta_page_access_token) setMetaToken(res.data.meta_page_access_token);
    } catch (err) { console.error('Fetch settings failed', err); }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/customers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (err) { console.error('Fetch customers failed', err); }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) { console.error('Fetch users failed', err); }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/users/roles', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(res.data);
    } catch (err) { console.error('Fetch roles failed', err); }
  };

  useEffect(() => {
    if (isLoggedIn) {
      if (user?.role === 'admin') {
        fetchRoles();
      }
    }
  }, [isLoggedIn, user]);

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/settings/update', { settings: metaSettings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã lưu cài đặt Meta thành công!');
    } catch (err) {
      addToast('Lỗi khi lưu cài đặt');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const success = await createLead(newLead);
    if (success) {
      setShowAddLeadModal(false);
      setNewLead({ 
        name: '', phone: '', email: '', gender: '', birth_date: '', 
        source: 'Messenger', tour_id: '', consultation_note: '', 
        bu_group: '', assigned_to: '', classification: 'Mới',
        last_contacted_at: ''
      });
    }
  };


  const createLead = async (leadData) => {
    setLoading(true);
    const cleanedData = {
      ...leadData,
      tour_id: leadData.tour_id || null,
      assigned_to: leadData.assigned_to || null
    };
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/leads', cleanedData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Idempotency-Key': typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `idemp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        }
      });
      addToast('Đã thêm Lead mới thành công!');
      fetchLeads();
      return true;
    } catch (err) {
      console.error(err);
      addToast('Lỗi khi thêm Lead mới');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async (leadId) => {
    if (!leadId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/notes/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadNotes(res.data);
    } catch (err) { console.error(err); }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/notes', { lead_id: selectedLeadForNotes.id, content: newNote }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewNote('');
      fetchNotes(selectedLeadForNotes.id);
      addToast('Đã lưu ghi chú mới');
    } catch (err) { console.error(err); }
  };

  const handleAddCustomerNote = async (e) => {
    e.preventDefault();
    if (!newCustomerNote.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/notes', { 
        customer_id: editingCustomer.id, 
        content: newCustomerNote 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCustomerNote('');
      addToast('Đã thêm ghi chú mới cho khách hàng.');
      // Refresh customer data to show new note in timeline
      const response = await axios.get(`/api/customers/${editingCustomer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingCustomer(response.data);
    } catch (err) {
      addToast('Lỗi khi thêm ghi chú: ' + err.message);
    }
  };

  const handleUpdateLead = async (e) => {
    if (e) e.preventDefault();
    if (!editingLead?.id) {
       addToast('Lỗi: Lead không có ID hợp lệ.');
       return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/leads/${editingLead.id}`, editingLead, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã cập nhật thông tin Lead thành công!');
      setEditingLead(null);
      fetchLeads();
    } catch (err) {
      console.error(err);
      addToast('Lỗi khi cập nhật Lead');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e) => {
    if (e) e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...newCustomer,
        birth_date: newCustomer.birth_date || null,
        id_expiry: newCustomer.id_expiry || null
      };
      await axios.post('/api/customers', dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCustomers();
      setShowAddCustomerModal(false);
      setNewCustomer({
        name: '', phone: '', email: '', gender: '', birth_date: '', 
        nationality: 'Việt Nam', id_card: '', id_expiry: '', address: '', 
        preferred_contact: 'Zalo', role: 'booker', customer_segment: 'New Customer',
        tour_interests: '', special_requests: '', internal_notes: '',
        location_city: '', travel_season: '', created_at: new Date().toLocaleDateString('en-CA')
      });
      addToast('Đã thêm khách hàng mới thành công.');
    } catch (err) {
      if (err.response && err.response.status === 409) {
        alert('⚠️ LỖI TRÙNG DỮ LIỆU: ' + err.response.data.message);
      } else {
        addToast(err.response?.data?.message || 'Lỗi khi thêm khách hàng');
      }
    }
  };

  const handleEditCustomer = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/customers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingCustomer(res.data);
    } catch (err) {
      addToast('Lỗi khi tải thông tin khách hàng');
      console.error(err);
    }
  };

  const handleUpdateCustomer = async (e) => {
    if (e) e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...editingCustomer,
      };
      if (dataToSend.created_at && dataToSend.created_at.includes('T')) delete dataToSend.created_at;
      if (dataToSend.first_deal_date && dataToSend.first_deal_date.includes('T')) delete dataToSend.first_deal_date;
      if (dataToSend.birth_date) dataToSend.birth_date = new Date(dataToSend.birth_date).toLocaleDateString('en-CA');
      if (dataToSend.id_expiry) dataToSend.id_expiry = new Date(dataToSend.id_expiry).toLocaleDateString('en-CA');
      await axios.put(`/api/customers/${editingCustomer.id}`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCustomers();
      setEditingCustomer(null);
      addToast('Đã cập nhật thông tin khách hàng.');
    } catch (err) {
      addToast('Lỗi khi cập nhật khách hàng');
    }
  };

  const handleDeleteCustomer = (id) => {
    setCustomerToDelete(id);
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/customers/${customerToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa khách hàng thành công');
    } catch (err) {
      addToast('Lỗi khi xóa khách hàng: ' + (err.response?.data?.message || err.message));
    } finally {
      setCustomerToDelete(null);
      fetchCustomers();
      setLoading(false);
    }
  };

  const handleDeleteTour = (id) => {
    setTourToDelete(id);
  };

  const confirmDeleteTour = async () => {
    if (!tourToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/tours/${tourToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa tour thành công');
    } catch (err) {
      addToast('Lỗi khi xóa tour: ' + (err.response?.data?.message || err.message));
    } finally {
      setTourToDelete(null);
      fetchTours();
      setLoading(false);
    }
  };

  const handleConvertLead = async (leadId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/customers/convert', { leadId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCustomers();
      fetchLeads();
      setEditingLead(null);
      if (res.data && res.data.customer && res.data.customer.was_existing_customer) {
        alert('TING TING 🌱 - Gắn KPI thành công!\n\nLưu ý: Hệ thống phát hiện đây là Khách Hàng Cũ quay lại nên đã tự động gộp Nhật Ký Tư Vấn và Thông tin đơn hàng mới vào Hồ Sơ Gốc của khách này để tăng LTV (Không tạo thêm Khách rác). Quá tuyệt vời!');
        addToast('Đã gộp Lead vào Khách quen thành công!');
      } else {
        addToast('Đã chuyển đổi Lead thành Khách hàng mới thành công!');
      }
    } catch (err) {
      addToast(err.response?.data?.message || 'Lỗi khi chuyển đổi Lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesStatus = !leadFilters.status || lead.status === leadFilters.status;
    const matchesSource = (() => {
      if (!leadFilters.source) return true;
      const target = leadFilters.source.toLowerCase();
      const s = (lead.source || '').toLowerCase();
      const hasFb = !!lead.facebook_psid || !!lead.meta_lead_id;
      const hasZalo = !!lead.zalo_uid;

      if (target === 'meta' || target === 'messenger') {
        return s.includes('messenger') || s.includes('meta') || s.includes('fb') || hasFb;
      }
      if (target === 'zalo') {
        return s.includes('zalo') || hasZalo;
      }
      if (target === 'tiktok') {
        return s.includes('tiktok');
      }
      if (target === 'hotline') {
        return s.includes('hotline') || s.includes('phone') || s.includes('điện thoại');
      }
      if (target === 'other' || target === 'khác') {
        return !s.includes('messenger') && !s.includes('meta') && !s.includes('fb') && !s.includes('zalo') && !s.includes('tiktok') && !s.includes('hotline') && !hasFb && !hasZalo;
      }
      return s === target;
    })();
    const matchesBU = !leadFilters.bu_group || (leadFilters.bu_group === 'NO_BU' ? !lead.bu_group : (leadFilters.bu_group === 'MY_BU' ? (user?.bus || []).includes(lead.bu_group) : lead.bu_group === leadFilters.bu_group));
    const matchesStaff = !leadFilters.assigned_to || (leadFilters.assigned_to === 'NO_STAFF' ? !lead.assigned_to : lead.assigned_to === Number(leadFilters.assigned_to));
    const matchesSearch = !leadFilters.search || 
      (lead.name?.toLowerCase().includes(leadFilters.search.toLowerCase())) ||
      (lead.phone?.includes(leadFilters.search));
    
    // Time range filtering
    let matchesTime = true;
    const dateField = leadFilters.dateField || 'created_at';

    const checkDateInRange = (rawDate) => {
      if (!rawDate) return false;
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return false;

      if (leadFilters.startDate || leadFilters.endDate) {
        if (leadFilters.startDate) {
          const [sy, sm, sd] = leadFilters.startDate.split('-').map(Number);
          const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
          if (d < start) return false;
        }
        if (leadFilters.endDate) {
          const [ey, em, ed] = leadFilters.endDate.split('-').map(Number);
          const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
          if (d > end) return false;
        }
        return true;
      }

      if (leadFilters.timeRange && leadFilters.timeRange !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (leadFilters.timeRange === 'today') {
          return d >= today;
        } else if (leadFilters.timeRange === 'yesterday') {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return d >= yesterday && d < today;
        } else if (leadFilters.timeRange === 'week') {
          const firstDayOfWeek = new Date(today);
          const day = firstDayOfWeek.getDay() || 7; 
          if (day !== 1) firstDayOfWeek.setHours(-24 * (day - 1));
          return d >= firstDayOfWeek;
        } else if (leadFilters.timeRange === 'month') {
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          return d >= firstDayOfMonth;
        } else if (leadFilters.timeRange === 'quarter') {
          const quarter = Math.floor(now.getMonth() / 3);
          const firstDayOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
          return d >= firstDayOfQuarter;
        } else if (leadFilters.timeRange?.startsWith('month_')) {
          const monthNum = parseInt(leadFilters.timeRange.replace('month_', ''), 10);
          const year = now.getFullYear();
          const startOfMonth = new Date(year, monthNum - 1, 1, 0, 0, 0, 0);
          const endOfMonth = new Date(year, monthNum, 0, 23, 59, 59, 999);
          return d >= startOfMonth && d <= endOfMonth;
        }
      }

      return true;
    };

    if (dateField === 'created_at') {
      matchesTime = checkDateInRange(lead.created_at);
    } else if (dateField === 'last_contacted_at') {
      matchesTime = checkDateInRange(lead.last_contacted_at);
    } else if (dateField === 'both') {
      matchesTime = checkDateInRange(lead.created_at) || (lead.last_contacted_at && checkDateInRange(lead.last_contacted_at));
    }

    let matchesTours = true;
    if (leadFilters.tours && leadFilters.tours.length > 0) {
      if (leadFilters.tours.includes('NO_TOUR')) {
        matchesTours = !lead.tour_id || leadFilters.tours.includes(String(lead.tour_id));
      } else {
        matchesTours = leadFilters.tours.includes(String(lead.tour_id));
      }
    }

    const matchesPhone = !leadFilters.hasPhone || 
      (leadFilters.hasPhone === 'yes' ? (lead.phone && lead.phone.trim() !== '') : (!lead.phone || lead.phone.trim() === ''));

    return matchesStatus && matchesSource && matchesSearch && matchesBU && matchesStaff && matchesTime && matchesTours && matchesPhone;
  });


  const handleInlineUpdate = async (id, field, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/leads/${id}`, { [field]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leads.map(lead => lead.id === id ? { ...lead, [field]: value } : lead));
      addToast(`Đã tự động lưu ${field}`);
    } catch (err) {
      console.error('Update failed:', err);
      addToast('Lỗi khi lưu dữ liệu');
    }
  };

  const fetchTours = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/tours', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTours(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBookings = async (page = bookingCurrentPage, filters = bookingFilters) => {
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams();
      qs.append('page', page);
      qs.append('limit', 30);
      if (filters.search) qs.append('search', filters.search);
      if (filters.bookingStatus) qs.append('status', filters.bookingStatus);
      if (filters.paymentStatus) qs.append('payment_status', filters.paymentStatus);

      const res = await axios.get(`/api/bookings?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(res.data)) {
        setBookings(res.data);
      } else {
        setBookings(res.data.data);
        setBookingTotalPages(res.data.totalPages);
        setBookingCurrentPage(res.data.page);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    const timer = setTimeout(() => {
      fetchBookings(bookingCurrentPage, bookingFilters);
    }, 500);
    return () => clearTimeout(timer);
  }, [bookingFilters, bookingCurrentPage, isLoggedIn]);

  const fetchLeads = async (isBackground = false) => {
    if (isBackground !== true) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Thêm cache-buster để tránh cache (đặc biệt từ Cloudflare) 
      const res = await axios.get(`/api/leads?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(res.data);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      if (isBackground !== true) setLoading(false);
    }
  };

  // Background polling cho tab Leads (mỗi 60 giây)
  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      // Chỉ poll nếu người dùng đang ở tab leads hoặc dashboard để tiết kiệm tài nguyên
      if (activeTab === 'leads' || activeTab === 'leads-dashboard') {
        fetchLeads(true); // background = true
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [isLoggedIn, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post('/api/auth/login', loginData);
      localStorage.setItem('token', res.data.token);
      const u = res.data.user;
      localStorage.setItem('user', JSON.stringify(u));
      setUser(u);
      setIsLoggedIn(true);
      
      const searchParams = new URLSearchParams(window.location.search);
      const redirectPath = searchParams.get('redirect');
      
      if (redirectPath) {
        navigate(redirectPath);
      } else if (u.role === 'sales' || u.role === 'sale' || u.role_name === 'sales' || u.role_name === 'sale') {
        navigate('/workspace');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Đăng nhập thất bại');
    }
  };

  const handleTestMeta = async (type = 'messenger') => {
    setTestLoading(true);
    try {
      const token = localStorage.getItem('token');
      let apiPath = '/api/messages/test-meta';
      const currentPageToken = metaSettings?.meta_page_access_token || metaToken;
      let payload = { token: currentPageToken };

      if (type === 'capi') {
        apiPath = '/api/settings/test-capi';
        payload = {};
      } else if (type === 'catalog') {
        apiPath = '/api/meta/catalog/sync-all';
        payload = {};
      }

      if (type === 'messenger' && !currentPageToken) {
        addToast('Vui lòng nhập Page Access Token trước khi kiểm tra!');
        setTestLoading(false);
        return;
      }

      const res = await axios.post(apiPath, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        addToast(res.data.message || 'Kiểm tra thành công!');
      } else {
        addToast(res.data.error || 'Kiểm tra thất bại.');
      }
    } catch (err) {
      addToast(err.response?.data?.error || err.response?.data?.message || 'Lỗi kết nối Meta.');
    } finally {
      setTestLoading(false);
      fetchSettings();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUser(null);
    navigate('/login');
  };

  const handleDeleteLead = (id) => {
    setLeadToDelete(id);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/leads/${leadToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa Lead thành công');
    } catch (err) {
      addToast('Lỗi khi xóa Lead: ' + (err.response?.data?.message || err.message));
    } finally {
      setLeadToDelete(null);
      fetchLeads();
      setLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/users', userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Thêm nhân viên mới thành công!');
      setShowAddUserModal(false);
      fetchUsers();
    } catch (err) {
      addToast(err.response?.data?.message || 'Lỗi khi thêm nhân viên');
    }
  };

  const handleUpdateUser = async (id, userData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/users/${id}`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Cập nhật nhân viên thành công!');
      setEditingUserAccount(null);
      fetchUsers();
    } catch (err) {
      addToast(err.response?.data?.message || 'Lỗi khi cập nhật nhân viên');
    }
  };

  const handleChangePasswordAdmin = async (id, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/users/${id}/password`, { newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã đổi mật khẩu nhân viên.');
      setUserToChangePassword(null);
    } catch (err) {
      addToast(err.response?.data?.message || 'Lỗi khi đổi mật khẩu');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToast('Đã xóa nhân viên.');
      fetchUsers();
    } catch (err) {
      addToast(err.response?.data?.message || 'Lỗi khi xóa nhân viên');
    }
  };

  const handleAddNoteForLead = async (leadId) => {
    if (!newNote.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/notes', 
        { lead_id: leadId, content: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeadNotes([res.data, ...leadNotes]);
      setNewNote('');
      fetchLeads(); // Refresh dashboard counts
    } catch (err) { addToast('Lỗi khi thêm ghi chú'); }
  };

  const renderDashboard = () => {
    const currentUserFull = users.find(u => u.id === user?.id) || {};
    const hasPerms = currentUserFull.permissions || {};
    
    // Hàm check quyền V2
    const checkPerm = (mod, action = 'view') => {
      if (user?.role === 'admin') return true;
      if (!myPermissions || !myPermissions[mod]) return false;
      return myPermissions[mod][action] === true;
    };

    const checkPermAny = (mod, actions = []) => {
      if (user?.role === 'admin') return true;
      if (!myPermissions || !myPermissions[mod]) return false;
      return actions.some(act => myPermissions[mod][act] === true);
    };

    const checkView = (mod) => {
      if (user?.role === 'admin') return true;
      // Trải nghiệm V2 mượt mà hơn cho Sidebar
      if (myPermissions) {
        return checkPermAny(mod, ['view', 'view_own', 'view_all']);
      }
      if (hasPerms[mod]) return hasPerms[mod].can_view === true;
      return false;
    };

    const isVisaProductViewer = ['admin', 'manager', 'group_manager', 'sales_lead', 'sale_lead', 'operations_lead', 'group_operations_lead'].includes((user?.role || user?.role_name || '').toLowerCase());




  // ── Route Guard: /tai-lieu/* và /hdv phải render DocumentsPage riêng, KHÔNG dùng sidebar ──
  if (location.pathname.startsWith('/tai-lieu') || location.pathname.startsWith('/hdv')) {
    return <DocumentsPage />;
  }
  if (location.pathname.startsWith('/huong-dan-erp/tao-nghi-phep')) {
    return <HuongDanTaoNghiPhepPage />;
  }
  if (location.pathname.startsWith('/huong-dan-erp/dat-phong-hop')) {
    return <HuongDanDatPhongHopPage />;
  }
  if (location.pathname.startsWith('/huong-dan-erp')) {
    return <HuongDanErpHub />;
  }


  return (
    <div className="app-container">
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)} />
      )}
      <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`} ref={sidebarRef}>
        <button className="mobile-sidebar-close-btn" onClick={() => setIsMobileMenuOpen(false)}>
          <X size={20} />
        </button>
        <button className="sidebar-toggle-btn" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} title="Thu/Phóng Menu">
          {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer', overflow: 'hidden' }}>
          <img src="/logo.png" alt="FIT TOUR" style={{ height: '40px', width: isSidebarCollapsed ? '32px' : 'auto', objectFit: isSidebarCollapsed ? 'cover' : 'contain', objectPosition: 'left' }} />
        </div>

        <div className="sidebar-nav-scroll">
          <div className="nav-section-title">Góc Cá Nhân</div>
          <Link title="Lối Tắt Làm Việc" to="/workspace" className={`nav-item ${activeTab === 'workspace' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
            <Home /> Lối Tắt Làm Việc</Link>

          <div className="nav-section-title">Tổng quan</div>
          <Link title="Dashboard" to="/dashboard" className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
            <LayoutDashboard /> Dashboard</Link>
          
          {(checkView('leads') || checkView('tours')) && (
            <>
              <div className="nav-section-title">Marketing & Sales</div>
              {checkView('leads') && (
                <Link title="Trung Tâm Điều Phối" 
                  to="/notification-center"
                  className={`nav-item ${['notification-center'].includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <MessageCircle /> Trung Tâm Điều Phối</Link>
              )}
              {checkView('leads') && (
                <Link title="Lead Marketing" 
                  to="/leads"
                  className={`nav-item ${activeTab === 'leads' || activeTab === 'leads-dashboard' ? 'active-parent' : ''}`} 
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ justifyContent: 'space-between' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('leads');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Users /> Lead Marketing</div>
                  <ChevronRight size={14} opacity={0.5} />
                </Link>
              )}



              <Link title="Dịch vụ Hỗ trợ" 
                to="/travel-support"
                className={`nav-item ${activeTab === 'travel-support' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Briefcase /> Dịch vụ Hỗ trợ</Link>
              {checkView('leads') && (
                <div title="Marketing Ads" 
                  className={`nav-item ${(activeTab === 'marketing-ads' || activeTab === 'management-dashboard' || activeTab === 'marketing-budget-plan') ? 'active' : ''}`} 
                  onClick={() => navigate('/marketing-ads')}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('marketing-ads');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Target /> Marketing Ads</div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}

              {checkView('leads') && (
                <div title="Messenger" 
                  className={`nav-item ${['inbox', 'message-templates'].includes(activeTab) ? 'active-parent' : ''}`}
                  onClick={() => navigate('/inbox')}
                  style={{ justifyContent: 'space-between', color: '#6366f1', background: 'rgba(99, 102, 241, 0.1)' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('messenger');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <MessageSquare size={18} /> Messenger
                  </div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}

              {checkView('leads') && (
                <div title="Zalo Chat" 
                  className={`nav-item ${['zalo-sandbox', 'zalo-ai-settings'].includes(activeTab) ? 'active-parent' : ''}`}
                  onClick={() => navigate('/zalo-sandbox')}
                  style={{ justifyContent: 'space-between', color: '#0ea5e9' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('zalo-chat');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <MessageCircle size={18} /> Zalo Chat
                  </div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}
            </>
          )}

          {(checkView('tours') || checkView('departures') || checkView('guides') || true) && (
            <>
              <div className="nav-section-title">Nghiệp vụ Lõi</div>
              
              {checkView('tours') && (
                <div title="Sản phẩm Tour" 
                  className={`nav-item ${activeTab === 'tours' || activeTab === 'bus' ? 'active-parent' : ''}`}
                  onClick={() => navigate('/tours')}
                  style={{ justifyContent: 'space-between' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('tours');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Package /> Sản phẩm Tour</div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}

              {checkView('op_tours') && (
                <div title="Lịch khởi hành" 
                  className={`nav-item ${activeTab === 'op-tours' || activeTab === 'costings' || activeTab === 'reminders' ? 'active-parent' : ''}`}
                  onClick={() => navigate('/op-tours')}
                  style={{ justifyContent: 'space-between' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('op-tours');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Calendar /> Lịch khởi hành</div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}

              {checkView('op_tours') && (
                 <div title="Công cụ OCR Hộ chiếu" className={`nav-item ${activeTab === 'passport-ocr' ? 'active' : ''}`} onClick={() => { navigate('/passport-ocr'); setActiveTab('passport-ocr'); }}>
                   <ScanText size={18} /> Công cụ OCR Hộ chiếu</div>
              )}

              {true && (
                <div title="Quản lý Visa" 
                  className={`nav-item ${['visas', 'visa-products', 'visa-form-templates'].includes(activeTab) ? 'active-parent' : ''}`}
                  onClick={() => navigate('/visas')}
                  style={{ justifyContent: 'space-between' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('visas');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <MapPin size={18} /> Quản lý Visa</div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}

              {checkView('op_tours') && (
                <div title="Phiếu Thu / Chi" 
                  className={`nav-item ${activeTab === 'vouchers' ? 'active' : ''}`}
                  onClick={() => navigate('/vouchers')}
                >
                  <DollarSign /> Phiếu Thu / Chi</div>
              )}

              {checkView('guides') && (
                <div title="Hướng dẫn viên" 
                  className={`nav-item ${activeTab === 'guides' ? 'active-parent' : ''}`} 
                  onClick={() => { navigate('/guides'); setActiveTab('guides'); setGuideActiveTab('list'); }}
                  style={{ justifyContent: 'space-between' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('guides');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Map /> Hướng dẫn viên</div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}
            </>
          )}

          {(checkView('bookings') || checkView('customers')) && (
            <>
              {checkView('bookings') && (
                <div title="Quản lý Booking" className={`nav-item ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => navigate('/bookings')}>
                  <ShoppingCart /> Quản lý Booking (Đăng ký)</div>
              )}
              {checkView('customers') && (
                <div title="Khách hàng" 
                  className={`nav-item ${activeTab === 'customers' ? 'active-parent' : ''}`} 
                  onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('list'); }}
                  style={{ justifyContent: 'space-between' }}
                  onMouseEnter={(e) => {
                    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredMenu('customers');
                  }}
                  onMouseLeave={() => {
                    menuTimerRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                    }, 150);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <UserCheck /> Khách hàng</div>
                  <ChevronRight size={14} opacity={0.5} />
                </div>
              )}
            </>
          )}

          <div className="nav-section-title">Đối tác & Dịch vụ</div>
          <div title="Nhà cung cấp" 
            className={`nav-item ${['hotels', 'restaurants', 'transports', 'tickets', 'airlines', 'landtours'].includes(activeTab) ? 'active-parent' : ''}`} 
            onClick={() => { navigate('/hotels'); setActiveTab('hotels'); }}
            style={{ justifyContent: 'space-between' }}
            onMouseEnter={(e) => {
              if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredRect(rect);
              setHoveredMenu('suppliers');
            }}
            onMouseLeave={() => {
              menuTimerRef.current = setTimeout(() => {
                setHoveredMenu(null);
              }, 150);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Package /> Nhà cung cấp</div>
            <ChevronRight size={14} opacity={0.5} />
          </div>

          {/* ═══ TOUR ĐOÀN SECTION ═══ */}
          {(['group_projects', 'group_leaders', 'mice_leads', 'marketing_ads'].some(mod => checkView(mod))) && (
            <>
              <div className="nav-section-title" style={{ color: '#d97706' }}>Tour Đoàn 🔒</div>
              {checkPerm('group_projects', 'view_dashboard') && (
                <div title="Dashboard MICE" 
                  className={`nav-item ${activeTab === 'group-dashboard' ? 'active' : ''}`} 
                  onClick={() => { navigate('/group/dashboard'); setActiveTab('group-dashboard'); }}
                >
                  <Activity /> Dashboard MICE</div>
              )}
              {checkView('group_leaders') && (
                <>
                  <div title="Doanh nghiệp" 
                    className={`nav-item ${activeTab === 'b2b-companies' ? 'active' : ''}`} 
                    onClick={() => { navigate('/group/companies'); setActiveTab('b2b-companies'); setB2bActiveTab('list'); }}
                    style={{ justifyContent: 'space-between' }}
                    onMouseEnter={(e) => {
                      if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                      setHoveredMenu('b2b-companies');
                      setHoveredRect(e.currentTarget.getBoundingClientRect());
                    }}
                    onMouseLeave={() => {
                      menuTimerRef.current = setTimeout(() => {
                        setHoveredMenu(null);
                      }, 150);
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Building /> Doanh nghiệp</div>
                    <ChevronRight size={14} opacity={0.5} />
                  </div>
                  <div title="Trưởng đoàn" 
                    className={`nav-item ${activeTab === 'group-leaders' ? 'active' : ''}`} 
                    onClick={() => { navigate('/group/leaders'); setActiveTab('group-leaders'); }}
                  >
                    <Users /> Trưởng đoàn</div>
                </>
              )}
              {checkView('group_projects') && (
                <div title="Dự án Tour (MICE)" 
                  className={`nav-item ${activeTab === 'group-projects' ? 'active' : ''}`} 
                  onClick={() => { navigate('/group/projects'); setActiveTab('group-projects'); }}
                >
                  <Briefcase /> Dự án Tour (MICE)</div>
              )}
              {checkView('mice_leads') && (
                <div title="MICE Leads (Tiềm năng)" 
                  className={`nav-item ${activeTab === 'group-mice-leads' ? 'active' : ''}`} 
                  onClick={() => { navigate('/group/mice-leads'); setActiveTab('group-mice-leads'); }}
                >
                  <Filter /> MICE Leads (Tiềm năng)</div>
              )}
              {(checkView('marketing_ads') || checkView('group_projects') || user?.role === 'admin') && (
                <div title="Báo Cáo Google Ads (BU3)" 
                  className={`nav-item ${activeTab === 'marketing-google-ads' ? 'active' : ''}`} 
                  onClick={() => { navigate('/group/google-ads'); setActiveTab('marketing-google-ads'); }}
                >
                  <Search /> Báo Cáo Google Ads (BU3)</div>
              )}

            </>
          )}

          <div className="nav-section-title">Hệ thống & Nhân sự</div>
          <div title="Quản lý Nhân sự" 
            className={`nav-item ${['users','staff-calendar','teams','team-directory','org-chart'].includes(activeTab) ? 'active' : ''}`} 
            onClick={() => { navigate('/team-directory'); setActiveTab('team-directory'); }}
            style={{ justifyContent: 'space-between' }}
            onMouseEnter={(e) => {
              if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredRect(rect);
              setHoveredMenu('hr-menu');
            }}
            onMouseLeave={() => {
              menuTimerRef.current = setTimeout(() => {
                setHoveredMenu(null);
              }, 150);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <UserCheck /> Quản lý Nhân sự</div>
            <ChevronRight size={14} opacity={0.5} />
          </div>

          {user && (user.role === 'admin' || user.role === 'manager') && (
            <div title="Cấu hình Email" 
              className={`nav-item ${['email-rules', 'email-groups', 'notification-dashboard'].includes(activeTab) ? 'active' : ''}`} 
              onClick={() => { navigate('/email-rules'); setActiveTab('email-rules'); }}
              style={{ justifyContent: 'space-between' }}
              onMouseEnter={(e) => {
                if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredRect(rect);
                setHoveredMenu('email-menu');
              }}
              onMouseLeave={() => {
                menuTimerRef.current = setTimeout(() => {
                  setHoveredMenu(null);
                }, 150);
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Mail /> Cấu hình Email</div>
              <ChevronRight size={14} opacity={0.5} />
            </div>
          )}

          {checkView('markets') && (
                <div title="Quản lý Thị trường" className={`nav-item ${activeTab === 'market-settings' ? 'active' : ''}`} onClick={() => navigate('/market-settings')}>
                  <MapPin /> Quản lý Thị trường</div>
              )}
              {checkView('settings') && (
                <>
                  <div title="Cấu hình Meta" className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => navigate('/settings')}>
                    <Settings /> Cấu hình Meta</div>
                  {user && (user.role === 'admin' || user.role === 'manager') && (
                    <div title="Quản lý Media" className={`nav-item ${activeTab === 'media-settings' ? 'active' : ''}`} onClick={() => navigate('/media-settings')}>
                      <ImageIcon /> Quản lý Media</div>
                  )}
                  <div title="Nhật ký hệ thống" className={`nav-item ${activeTab === 'audit-logs' ? 'active' : ''}`} onClick={() => navigate('/audit-logs')}>
                    <Activity /> Nhật ký hệ thống</div>
                  <div title="AI Agent Manager" className={`nav-item ${activeTab === 'agent-manager' ? 'active' : ''}`} onClick={() => navigate('/agent-manager')}>
                    <Cpu /> AI Agent Manager</div>
                </>
              )}

          <div className="nav-section-title">Tài Liệu Nội Bộ</div>
          <div title="Sổ Tay FIT Tour" className={`nav-item ${activeTab === 'tai-lieu' ? 'active' : ''}`} onClick={() => navigate('/tai-lieu')}>
            <BookOpen /> Sổ Tay FIT Tour</div>
          <div title="Luồng xử lý CRM" className={`nav-item ${activeTab === 'workflow' ? 'active' : ''}`} onClick={() => navigate('/workflow')}>
            <FileText /> Luồng xử lý CRM</div>


          <div title="Biểu Mẫu & Văn Bản" 
            className={`nav-item ${['licenses', 'announcements'].includes(activeTab) ? 'active' : ''}`} 
            onClick={() => navigate('/licenses')}
            style={{ justifyContent: 'space-between' }}
            onMouseEnter={(e) => {
              if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredRect(rect);
              setHoveredMenu('licenses-menu');
            }}
            onMouseLeave={() => {
              menuTimerRef.current = setTimeout(() => {
                setHoveredMenu(null);
              }, 150);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileText /> Biểu Mẫu & Văn Bản</div>
            <ChevronRight size={14} opacity={0.5} />
          </div>
          <div title="Quy tắc chọn BU" className={`nav-item ${activeTab === 'bu-rules' ? 'active' : ''}`} onClick={() => navigate('/bu-rules')}>
            <FileText /> Quy tắc chọn BU</div>

          {checkView('departures') && (
            <>
              <div className="nav-section-title">Khách hàng Công Khai</div>
              <div title="Quản lý Thẻ HDV" className={`nav-item ${activeTab === 'admin-trip' ? 'active' : ''}`} onClick={() => { navigate('/admin-trip'); setActiveTab('admin-trip'); }} style={{ color: '#0ea5e9' }}>
                <BookOpen size={18} /> Quản lý Thẻ HDV</div>
            </>
          )}

          <div className="nav-section-title">Trợ giúp & Hướng dẫn</div>
          <div title="Sổ tay Hướng dẫn" 
            className={`nav-item ${activeTab === 'manual' ? 'active-parent' : ''}`} 
            onClick={() => navigate('/manual/overview')}
            style={{ justifyContent: 'space-between' }}
            onMouseEnter={(e) => {
              if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredRect(rect);
              setHoveredMenu('manual');
            }}
            onMouseLeave={() => {
              menuTimerRef.current = setTimeout(() => {
                setHoveredMenu(null);
              }, 150);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BookOpen size={18} /> Sổ tay Hướng dẫn</div>
            <ChevronRight size={14} opacity={0.5} />
          </div>

          <div style={{ marginTop: 'auto', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <div title="TRANG CÁ NHÂN" className={`nav-item ${activeTab === 'my-profile' ? 'active' : ''}`} onClick={() => { navigate('/my-profile'); setActiveTab('my-profile'); }} style={{ color: '#a78bfa', background: activeTab === 'my-profile' ? 'rgba(167, 139, 250, 0.1)' : 'rgba(167, 139, 250, 0.03)', marginBottom: '5px' }}>
              <User size={18} /> TRANG CÁ NHÂN
            </div>
            <div className="nav-item" onClick={() => setShowChangeMyPassword(true)} style={{ color: '#0ea5e9', background: 'rgba(14, 165, 233, 0.05)', marginBottom: '5px' }}>
              <Lock size={18} /> <strong>ĐỔI MẬT KHẨU</strong>
            </div>
            <div className="nav-item" onClick={handleLogout} style={{ color: '#f87171', background: 'rgba(239, 68, 68, 0.05)' }}>
              <LogOut size={18} /> <strong>ĐĂNG XUẤT</strong>
            </div>
          </div>
        </div>
      </aside>

      {/* Portal-like Flyout Menus */}
      {hoveredMenu === 'tours' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('tours');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'tours' ? 'active' : ''}`} 
            onClick={() => { navigate('/tours'); setHoveredMenu(null); }}
          >
            Danh sách Sản phẩm
          </div>
          <div 
            className={`submenu-item ${activeTab === 'bus' ? 'active' : ''}`} 
            onClick={() => { navigate('/bus'); setHoveredMenu(null); }} 
          >
            Quản lý Khối BU
          </div>
        </div>
      )}

      {hoveredMenu === 'leads' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${Math.max(10, Math.min(hoveredRect.top, window.innerHeight - 350))}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('leads');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'leads' ? 'active' : ''}`} 
            onClick={() => { navigate('/leads'); setHoveredMenu(null); }}
          >
            Danh sách Lead
          </div>
          <div 
            className={`submenu-item ${activeTab === 'leads-dashboard' ? 'active' : ''}`} 
            onClick={() => { navigate('/leads-dashboard'); setHoveredMenu(null); }} 
          >
            Dashboard Thống kê
          </div>
          <div 
            className={`submenu-item ${activeTab === 'zalo-sandbox' ? 'active' : ''}`} 
            onClick={() => { navigate('/zalo-sandbox'); setHoveredMenu(null); }} 
          >
            Zalo Chat
          </div>
          <div 
            className={`submenu-item ${activeTab === 'zalo-ai-settings' ? 'active' : ''}`} 
            onClick={() => { navigate('/zalo-ai-settings'); setHoveredMenu(null); }} 
            style={{ color: '#60a5fa', fontWeight: 500 }}
          >
            🤖 Cài đặt AI Agent (Zalo)
          </div>
          <div 
            className={`submenu-item ${activeTab === 'staff-performance' ? 'active' : ''}`} 
            onClick={() => { navigate('/staff-performance'); setHoveredMenu(null); }} 
          >
            Hiệu suất Nhân viên
          </div>
          <div 
            className={`submenu-item ${activeTab === 'dispatch-schedule' ? 'active' : ''}`} 
            onClick={() => { navigate('/dispatch-schedule'); setHoveredMenu(null); }} 
          >
            Đăng ký lịch trực
          </div>
          <div 
            className="submenu-item" 
            onClick={() => { navigate('/tai-lieu/marketing/quy-tac-phan-loai'); setHoveredMenu(null); }} 
            style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '4px', paddingTop: '8px' }}
          >
            Quy tắc phân loại
          </div>
        </div>
      )}


      {hoveredMenu === 'marketing-ads' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('marketing-ads');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'marketing-ads' ? 'active' : ''}`} 
            onClick={() => { navigate('/marketing-ads'); setHoveredMenu(null); }} 
          >
            Quản trị Meta Ads (Data/KPI)
          </div>
          <div 
            className={`submenu-item ${activeTab === 'marketing-budget-plan' ? 'active' : ''}`} 
            onClick={() => { navigate('/marketing-budget-plan'); setHoveredMenu(null); }} 
            style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
              marginTop: '4px', 
              paddingTop: '8px', 
              color: activeTab === 'marketing-budget-plan' ? '#38bdf8' : '#bae6fd', 
              fontWeight: 700 
            }}
          >
            📋 Kế Hoạch & Ngân Sách MKT
          </div>
          <div 
            className={`submenu-item ${activeTab === 'management-dashboard' ? 'active' : ''}`} 
            onClick={() => { navigate('/management-dashboard'); setHoveredMenu(null); }} 
            style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.08)', 
              marginTop: '4px', 
              paddingTop: '8px', 
              color: '#f472b6', 
              fontWeight: 700 
            }}
          >
            📈 Tổng Quan Marketing
          </div>
        </div>
      )}

      {hoveredMenu === 'messenger' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('messenger');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'inbox' ? 'active' : ''}`} 
            onClick={() => { navigate('/inbox'); setHoveredMenu(null); }}
          >
            Hộp thư đến
          </div>
          <div 
            className={`submenu-item ${activeTab === 'message-templates' ? 'active' : ''}`} 
            onClick={() => { navigate('/message-templates'); setHoveredMenu(null); }} 
          >
            Mẫu tin nhắn Messenger
          </div>
        </div>
      )}

      {hoveredMenu === 'zalo-chat' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('zalo-chat');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'zalo-sandbox' ? 'active' : ''}`} 
            onClick={() => { navigate('/zalo-sandbox'); setHoveredMenu(null); }}
          >
            💬 Hội thoại Zalo Chat
          </div>
          <div 
            className={`submenu-item ${activeTab === 'zalo-ai-settings' ? 'active' : ''}`} 
            onClick={() => { navigate('/zalo-ai-settings'); setHoveredMenu(null); }} 
            style={{ color: '#0ea5e9', fontWeight: 600 }}
          >
            🤖 Cài đặt AI Agent (RAG)
          </div>
        </div>
      )}

      {hoveredMenu === 'visas' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('visas');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div className="submenu-content" style={{ display: 'flex', flexDirection: 'column' }}>
            {isVisaProductViewer && (
            <div className={`submenu-item ${activeTab === 'visa-products' ? 'active' : ''}`} onClick={() => { navigate('/visa-products'); setHoveredMenu(null); }}>
              Sản phẩm Visa
            </div>
            )}
            <div className={`submenu-item ${activeTab === 'visas' ? 'active' : ''}`} onClick={() => { navigate('/visas'); setHoveredMenu(null); }}>
              Danh sách Hồ sơ
            </div>
            <div className={`submenu-item ${activeTab === 'visa-form-templates' ? 'active' : ''}`} onClick={() => { navigate('/visa-form-templates'); setHoveredMenu(null); }}>
              Mẫu Form Visa
            </div>
          </div>
        </div>
      )}

      {/* Old departures flyout removed — replaced by op-tours flyout */}

      {hoveredMenu === 'op-tours' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('op-tours');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'op-tours' ? 'active' : ''}`} 
            onClick={() => { navigate('/op-tours'); setHoveredMenu(null); }}
          >
            Danh sách Lịch khởi hành
          </div>
          {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'ceo') && (
            <div 
              className={`submenu-item ${activeTab === 'ceo-departures-dashboard' ? 'active' : ''}`} 
              onClick={() => { navigate('/ceo-departures-dashboard'); setHoveredMenu(null); }} 
            >
              📊 Dashboard LKH
            </div>
          )}
          {(user?.role === 'admin' || user?.role === 'operations' || user?.role === 'manager') && (
            <div 
              className={`submenu-item ${activeTab === 'costings' ? 'active' : ''}`} 
              onClick={() => { navigate('/costings'); setHoveredMenu(null); }} 
              style={{ color: '#94a3b8' }}
            >
              Bảng Dự Toán Tour (Draft)
            </div>
          )}
          <div 
            className={`submenu-item ${activeTab === 'reminders' ? 'active' : ''}`} 
            onClick={() => { navigate('/reminders'); setHoveredMenu(null); }}
            style={{ color: '#94a3b8' }}
          >
            Tiến độ Chăm sóc (Draft)
          </div>
          <div 
            className={`submenu-item ${activeTab === 'zns-demo' ? 'active' : ''}`} 
            onClick={() => { navigate('/zns-demo'); setActiveTab('zns-demo'); setHoveredMenu(null); }}
            style={{ color: '#0284c7', fontWeight: 600 }}
          >
            ZNS Demo (Mock)
          </div>
        </div>
      )}

      {hoveredMenu === 'guides' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('guides');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'guides' && guideActiveTab === 'list' ? 'active' : ''}`} 
            onClick={() => { navigate('/guides'); setActiveTab('guides'); setGuideActiveTab('list'); setHoveredMenu(null); }}
          >
            Danh sách HDV
          </div>
          <div 
            className={`submenu-item ${activeTab === 'guides' && guideActiveTab === 'timeline' ? 'active' : ''}`} 
            onClick={() => { navigate('/guides/timeline'); setActiveTab('guides'); setGuideActiveTab('timeline'); setHoveredMenu(null); }}
          >
            Lịch phân công
          </div>
          <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
          <div 
            className={`submenu-item ${activeTab === 'guides' && guideActiveTab === 'reviews' ? 'active' : ''}`} 
            onClick={() => { navigate('/guides/reviews'); setActiveTab('guides'); setGuideActiveTab('reviews'); setHoveredMenu(null); }}
          >
            ⭐ Đánh giá của KH
          </div>
          <div 
            className={`submenu-item ${activeTab === 'guides' && guideActiveTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => { navigate('/guides/dashboard'); setActiveTab('guides'); setGuideActiveTab('dashboard'); setHoveredMenu(null); }}
          >
            Dashboard Thống kê
          </div>
        </div>
      )}

      {hoveredMenu === 'customers' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${Math.max(10, Math.min(hoveredRect.top, window.innerHeight - 360))}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('customers');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'list' ? 'active' : ''}`} 
            onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('list'); setHoveredMenu(null); }}
          >
            📋 Danh sách Khách hàng
          </div>
          <div 
            className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'analytics' ? 'active' : ''}`} 
            onClick={() => { navigate('/customers/analytics'); setActiveTab('customers'); setCustomerActiveTab('analytics'); setHoveredMenu(null); }}
          >
            📊 Dashboard & Kiểm toán
          </div>
          <div 
            className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'calendar' ? 'active' : ''}`} 
            onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('calendar'); setHoveredMenu(null); }}
          >
            📅 Lịch Chăm sóc
          </div>
          <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
          <div 
            className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'cskh-board' ? 'active' : ''}`} 
            onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('cskh-board'); setHoveredMenu(null); }}
          >
            🏥 CSKH Board
          </div>
          <div 
            className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'cskh-todo' ? 'active' : ''}`} 
            onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('cskh-todo'); setHoveredMenu(null); }}
          >
            ✅ Todo List
          </div>
          <div 
            className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'cskh-search' ? 'active' : ''}`} 
            onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('cskh-search'); setHoveredMenu(null); }}
          >
            🔍 Tìm KH hàng loạt
          </div>
          {['admin', 'manager'].includes(user?.role) && (
            <div 
              className={`submenu-item ${activeTab === 'customers' && customerActiveTab === 'cskh-rules' ? 'active' : ''}`} 
              onClick={() => { navigate('/customers'); setActiveTab('customers'); setCustomerActiveTab('cskh-rules'); setHoveredMenu(null); }}
            >
              ⚙️ Cấu hình Rules
            </div>
          )}
        </div>
      )}

      {hoveredMenu === 'suppliers' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${Math.max(10, Math.min(hoveredRect.top, window.innerHeight - 380))}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('suppliers');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'hotels' ? 'active' : ''}`} 
            onClick={() => { navigate('/hotels'); setActiveTab('hotels'); setHoveredMenu(null); }}
          >
            Quản lý Khách sạn
          </div>
          <div 
            className={`submenu-item ${activeTab === 'restaurants' ? 'active' : ''}`} 
            onClick={() => { navigate('/restaurants'); setActiveTab('restaurants'); setHoveredMenu(null); }}
          >
            Quản lý Nhà hàng
          </div>
          <div 
            className={`submenu-item ${activeTab === 'transports' ? 'active' : ''}`} 
            onClick={() => { navigate('/transports'); setActiveTab('transports'); setHoveredMenu(null); }}
          >
            Quản lý Nhà xe
          </div>
          <div 
            className={`submenu-item ${activeTab === 'tickets' ? 'active' : ''}`} 
            onClick={() => { navigate('/tickets'); setActiveTab('tickets'); setHoveredMenu(null); }}
          >
            Quản lý Vé Tham Quan
          </div>
          <div 
            className={`submenu-item ${activeTab === 'airlines' ? 'active' : ''}`} 
            onClick={() => { navigate('/airlines'); setActiveTab('airlines'); setHoveredMenu(null); }}
          >
            Quản lý Vé máy bay
          </div>
          <div 
            className={`submenu-item ${activeTab === 'landtours' ? 'active' : ''}`} 
            onClick={() => { navigate('/landtours'); setActiveTab('landtours'); setHoveredMenu(null); }}
          >
            Quản lý Land Tour
          </div>
          <div 
            className={`submenu-item ${activeTab === 'insurances' ? 'active' : ''}`} 
            onClick={() => { navigate('/insurances'); setActiveTab('insurances'); setHoveredMenu(null); }}
          >
            Quản lý Bảo Hiểm
          </div>
          <div 
            className={`submenu-item ${activeTab === 'visa-providers' ? 'active' : ''}`} 
            onClick={() => { navigate('/visa-providers'); setActiveTab('visa-providers'); setHoveredMenu(null); }}
          >
            Quản lý Visa
          </div>
        </div>
      )}

      {hoveredMenu === 'hr-menu' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${Math.max(10, Math.min(hoveredRect.top, window.innerHeight - 360))}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('hr-menu');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'team-directory' ? 'active' : ''}`} 
            onClick={() => { navigate('/team-directory'); setActiveTab('team-directory'); setHoveredMenu(null); }}
          >
            📋 Danh bạ FIT Tour
          </div>
          <div 
            className={`submenu-item ${activeTab === 'leaves' ? 'active' : ''}`} 
            onClick={() => { navigate('/leaves'); setActiveTab('leaves'); setHoveredMenu(null); }}
          >
            🌴 Quản lý Nghỉ Phép
          </div>
          <div 
            className={`submenu-item ${activeTab === 'meeting-rooms' ? 'active' : ''}`} 
            onClick={() => { navigate('/meeting-rooms'); setActiveTab('meeting-rooms'); setHoveredMenu(null); }}
          >
            🗓️ Đặt Phòng Họp
          </div>
          <div 
            className={`submenu-item ${activeTab === 'org-chart' ? 'active' : ''}`} 
            onClick={() => { navigate('/org-chart'); setActiveTab('org-chart'); setHoveredMenu(null); }}
          >
            📊 Sơ đồ Tổ chức
          </div>
          <div 
            className={`submenu-item ${activeTab === 'staff-calendar' ? 'active' : ''}`} 
            onClick={() => { navigate('/staff-calendar'); setActiveTab('staff-calendar'); setHoveredMenu(null); }}
          >
            🎂 Lịch Nhân sự
          </div>
          {checkView('users') && (
            <>
              {(user?.role === 'admin') && (
                <div 
                  className={`submenu-item ${activeTab === 'teams' ? 'active' : ''}`} 
                  onClick={() => { navigate('/teams'); setActiveTab('teams'); setHoveredMenu(null); }}
                >
                  🏢 Quản lý Team
                </div>
              )}
              <div 
                className={`submenu-item ${activeTab === 'users' ? 'active' : ''}`} 
                onClick={() => { navigate('/users'); setActiveTab('users'); setHoveredMenu(null); }}
              >
                👥 Danh sách Nhân sự
              </div>
              {checkPerm('users', 'change_permissions') && (
                <div 
                  className="submenu-item" 
                  onClick={() => { setShowRolePermModal(true); setHoveredMenu(null); }}
                >
                  🛡 Phân Quyền Chức vụ
                </div>
              )}
            </>
          )}
        </div>
      )}

      {hoveredMenu === 'b2b-companies' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('b2b-companies');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'b2b-companies' && b2bActiveTab === 'list' ? 'active' : ''}`} 
            onClick={() => { navigate('/group/companies'); setActiveTab('b2b-companies'); setB2bActiveTab('list'); setHoveredMenu(null); }}
          >
            Danh sách Doanh nghiệp
          </div>
          <div 
            className={`submenu-item ${activeTab === 'b2b-companies' && b2bActiveTab === 'calendar' ? 'active' : ''}`} 
            onClick={() => { navigate('/group/companies?tab=calendar'); setActiveTab('b2b-companies'); setB2bActiveTab('calendar'); setHoveredMenu(null); }}
          >
            Lịch Chăm Sóc B2B
          </div>
        </div>
      )}

      {hoveredMenu === 'email-menu' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('email-menu');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'email-rules' ? 'active' : ''}`} 
            onClick={() => { navigate('/email-rules'); setActiveTab('email-rules'); setHoveredMenu(null); }}
          >
            ⚙️ Cấu hình Gửi (Triggers)
          </div>
          <div 
            className={`submenu-item ${activeTab === 'email-groups' ? 'active' : ''}`} 
            onClick={() => { navigate('/email-groups'); setActiveTab('email-groups'); setHoveredMenu(null); }}
          >
            👥 Nhóm Email (Groups)
          </div>
          <div 
            className={`submenu-item ${activeTab === 'notification-dashboard' ? 'active' : ''}`} 
            onClick={() => { navigate('/notification-dashboard'); setActiveTab('notification-dashboard'); setHoveredMenu(null); }}
          >
            📊 Logs Gửi Email
          </div>
        </div>
      )}

      {hoveredMenu === 'licenses-menu' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${hoveredRect.top}px`, 
            display: 'flex', 
            opacity: 1, 
            transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('licenses-menu');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div 
            className={`submenu-item ${activeTab === 'licenses' ? 'active' : ''}`} 
            onClick={() => { navigate('/licenses?tab=licenses'); setActiveTab('licenses'); setHoveredMenu(null); }}
          >
            📋 Biểu Mẫu Văn Phòng
          </div>
          <div 
            className={`submenu-item ${activeTab === 'announcements' ? 'active' : ''}`} 
            onClick={() => { navigate('/licenses?tab=announcements'); setActiveTab('licenses'); setHoveredMenu(null); }}
          >
            📜 Văn Bản Thông Báo
          </div>
        </div>
      )}

      
      {hoveredMenu === 'manual' && hoveredRect && (
        <div 
          className="submenu-flyout"
          style={{ 
            position: 'fixed', 
            left: `${hoveredRect.right + 5}px`, 
            top: `${Math.min(hoveredRect.top, window.innerHeight - 400)}px`, 
            display: 'flex', 
            opacity: 1, 
             transform: 'none',
            pointerEvents: 'auto',
            zIndex: 9999,
            maxHeight: '400px',
            flexDirection: 'column',
            overflowY: 'auto'
          }}
          onMouseEnter={() => {
            if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
            setHoveredMenu('manual');
          }}
          onMouseLeave={() => {
            menuTimerRef.current = setTimeout(() => {
              setHoveredMenu(null);
            }, 150);
          }}
        >
          <div className="submenu-item" style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', padding: '8px 16px', background: 'transparent' }}>
            Giới thiệu & Cơ Bản
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && (!pathParts[1] || pathParts[1] === 'overview') ? 'active' : ''}`} onClick={() => { navigate('/manual/overview'); setHoveredMenu(null); }}>
            Phân quyền hệ thống
          </div>
          
          <div className="submenu-item" style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', padding: '8px 16px', background: 'transparent', marginTop: '4px' }}>
            Sales & Marketing
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'leads-sop' ? 'active' : ''}`} onClick={() => { navigate('/manual/leads-sop'); setHoveredMenu(null); }}>
            Lead - Quy Định (SOP)
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'leads-guide' ? 'active' : ''}`} onClick={() => { navigate('/manual/leads-guide'); setHoveredMenu(null); }}>
            Lead - HD Sử Dụng
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'customers-sop' ? 'active' : ''}`} onClick={() => { navigate('/manual/customers-sop'); setHoveredMenu(null); }}>
            Khách Hàng - Quy Định
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'customers-guide' ? 'active' : ''}`} onClick={() => { navigate('/manual/customers-guide'); setHoveredMenu(null); }}>
            Khách Hàng - HD Sử Dụng
          </div>

          <div className="submenu-item" style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', padding: '8px 16px', background: 'transparent', marginTop: '4px' }}>
            Điều hành & Lõi
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'bookings' ? 'active' : ''}`} onClick={() => { navigate('/manual/bookings'); setHoveredMenu(null); }}>
            Đơn hàng & Dòng tiền
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'tours-sop' ? 'active' : ''}`} onClick={() => { navigate('/manual/tours-sop'); setHoveredMenu(null); }}>
            Tour - Quy Định (SOP)
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'tours-guide' ? 'active' : ''}`} onClick={() => { navigate('/manual/tours-guide'); setHoveredMenu(null); }}>
            Tour - HD Sử Dụng
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'departures-sop' ? 'active' : ''}`} onClick={() => { navigate('/manual/departures-sop'); setHoveredMenu(null); }}>
            Lịch KH - Quy Định
          </div>
          <div className={`submenu-item ${activeTab === 'manual' && pathParts[1] === 'departures-guide' ? 'active' : ''}`} onClick={() => { navigate('/manual/departures-guide'); setHoveredMenu(null); }}>
            Lịch KH - HD Sử Dụng
          </div>
        </div>
      )}

      <main className="main-content">
        <div className="breadcrumb-container">
          <div className="breadcrumb">CRM / {
              activeTab === 'op-tours' ? 'LỊCH KHỞI HÀNH' : 
              activeTab === 'management-dashboard' ? 'TỔNG QUAN MARKETING' : 
              activeTab === 'marketing-google-ads' ? 'TOUR ĐOÀN / GOOGLE ADS BU3' : 
              activeTab === 'notification-center' ? 'TRUNG TÂM ĐIỀU PHỐI' :
              activeTab.toUpperCase()
          }</div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          </div>
        </div>

        <header className={`header ${isHeaderHidden ? 'header-scroll-hidden' : ''}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button className="mobile-header-menu-btn" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu size={20} />
            </button>
            <h1>{
            activeTab === 'dashboard' ? 'Tổng quan hệ thống' : 
            activeTab === 'leads' ? 'Quản lý Lead Marketing' : 
            activeTab === 'leads-dashboard' ? 'Dashboard Lead Marketing' :
            activeTab === 'management-dashboard' ? 'Tổng quan Marketing' :
            activeTab === 'marketing-google-ads' ? 'Báo Cáo Google Ads (BU3 B2B)' :
            activeTab === 'bus' ? 'Quản lý Khối Kinh doanh (BU)' :
            activeTab === 'licenses' ? 'Biểu Mẫu & Văn Bản Văn Phòng' :
            activeTab === 'announcements' ? 'Văn Bản Thông Báo' :
            activeTab === 'bu-rules' ? 'Quy tắc chọn BU cho Lead' :
            activeTab === 'manual' ? 'Sổ tay HDSD CRM' :
            activeTab === 'op-tours' ? 'Lịch khởi hành' :
            activeTab === 'ceo-departures-dashboard' ? 'Dashboard Lịch Khởi Hành' :
            activeTab === 'team-directory' ? 'Nhân Sự FIT Tour' :
            activeTab === 'my-profile' ? 'Trang cá nhân' :
            activeTab === 'notification-center' ? 'Trung tâm điều phối' :
            activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
          }</h1>
            {/* Global Search Dummy Input */}
            <div 
              className="global-search-trigger"
              style={{
                display: 'flex', alignItems: 'center', backgroundColor: '#f1f5f9', cursor: 'text',
                padding: '6px 16px', borderRadius: '20px', border: '1px solid #e2e8f0',
                color: '#64748b', fontSize: '14px', gap: '8px'
              }}
              onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            >
              <Search size={16} />
              <span className="search-text" style={{ flex: 1 }}>Tìm kiếm...</span>
              <kbd className="search-kbd" style={{ backgroundColor: '#ffffff', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', border: '1px solid #cbd5e1' }}>Cmd K</kbd>
            </div>
          </div>
          <div className="header-right-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <NotificationBell currentUser={user} />
            <div 
              className="user-profile" 
              style={{ cursor: 'pointer', position: 'relative' }} 
              onClick={(e) => {
                e.stopPropagation();
                if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
                setHoveredMenu(hoveredMenu === 'user-profile' ? null : 'user-profile');
              }}
              onMouseEnter={() => {
                if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
              }}
              onMouseLeave={() => {
                menuTimerRef.current = setTimeout(() => {
                  if (hoveredMenu === 'user-profile') setHoveredMenu(null);
                }, 300);
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title="Mở tuỳ chọn">
                <div className="user-profile-info" style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{user?.full_name || user?.username || 'Người dùng'}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{user?.role?.toUpperCase() || 'NHÂN VIÊN'}</div>
                </div>
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="Avatar" className="user-avatar-img" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }} />
                ) : (
                  <div className="user-avatar" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: '#4f46e5', color: 'white', fontWeight: 'bold' }}>
                    {user?.full_name?.substring(0,1).toUpperCase() || user?.username?.substring(0,1).toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              
              {hoveredMenu === 'user-profile' && (
                <div 
                  className="user-profile-dropdown"
                  style={{ 
                    position: 'absolute', 
                    right: 0, 
                    display: 'flex', 
                    flexDirection: 'column',
                    opacity: 1, 
                    transform: 'none',
                    pointerEvents: 'auto',
                    zIndex: 9999,
                    top: 'calc(100% + 5px)',
                    minWidth: '170px',
                    background: '#fff',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <div className="mobile-only-user-header" style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>{user?.full_name || user?.username || 'Người dùng'}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{user?.role?.toUpperCase() || 'NHÂN VIÊN'}</div>
                  </div>
                  <div 
                    className="header-dropdown-item" 
                    onClick={(e) => { e.stopPropagation(); navigate('/my-profile'); setHoveredMenu(null); }}
                    style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                  >
                    📝 Edit info
                  </div>
                  <a 
                    href={`${window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://erp.fittour.vn'}/api/auth/google?sync_token=${localStorage.getItem('token')}`}
                    className="header-dropdown-item" 
                    style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: '#1e293b' }}
                  >
                    <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: '14px', height: '14px' }} />
                    Đồng bộ Gmail
                  </a>
                  <div 
                    className="header-dropdown-item" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      requestSubscription(); 
                      setHoveredMenu(null); 
                    }}
                    style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: 'bold' }}
                  >
                    🔔 Nhận Thông báo đẩy
                  </div>
                  <div 
                    className="header-dropdown-item" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      navigate('/meeting-rooms');
                      setActiveTab('meeting-rooms');
                      setHoveredMenu(null); 
                    }}
                    style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', color: '#0071e3', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    🏢 Xem Phòng Họp
                  </div>
                  <div 
                    className="header-dropdown-item" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setShowLeaveModal(true); 
                      setHoveredMenu(null); 
                    }}
                    style={{ padding: '12px 16px', color: '#8b5cf6', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    🌴 Xin nghỉ phép
                  </div>
                  <div 
                    className="header-dropdown-item" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      navigate('/staff-calendar');
                      setActiveTab('staff-calendar');
                      setHoveredMenu(null); 
                    }}
                    style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', cursor: 'pointer' }}
                  >
                    🎂 Lịch Nhân sự
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>



        <SystemAnnouncementPopup currentUser={user} />

        <CommandPalette onNavigate={(id) => {
            if (VALID_TABS.includes(id)) {
                setActiveTab(id);
            }
        }} />

        {/* AI Copilot Widget */}

        {(activeTab === 'dashboard' || activeTab === 'leads' || activeTab === 'inbox') && editingLead ? (
          <EditLeadModal 
            editingLead={editingLead}
            setEditingLead={setEditingLead}
            handleUpdateLead={handleUpdateLead}
            handleConvertLead={(leadId) => setLeadToConvert(leads.find(l => l.id === leadId))}
            LEAD_SOURCES={LEAD_SOURCES}
            LEAD_CLASSIFICATIONS={LEAD_CLASSIFICATIONS}
            LEAD_STATUSES={LEAD_STATUSES}
            tours={tourTemplates}
            users={users}
            leadNotes={leadNotes}
            newNote={newNote}
            setNewNote={setNewNote}
            handleAddNoteForLead={handleAddNoteForLead}
            bus={bus.filter(b => b.is_active !== false || b.id === editingLead.bu_group)}
            loading={loading}
          />
        ) : (
          <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', padding: '5rem' }}><div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #10b981', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div><style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style></div>}>
            {activeTab === 'workspace' && (
              <WorkspaceTab 
                currentUser={user}
                leads={leads}
                setEditingLead={setEditingLead}
                setShowAddLeadModal={setShowAddLeadModal}
                bookings={bookings}
                customers={customers}
                departures={tourDepartures}
                tourTemplates={tourTemplates}
                users={users}
                checkPerm={checkPerm}
                setShowLeaveModal={setShowLeaveModal}
                navigateToInbox={handleOpenInboxDrawer}
                openZaloDrawer={handleOpenZaloDrawer}
                fetchLeads={fetchLeads}
                handleConvertLead={(leadId) => setLeadToConvert(leads.find(l => l.id === leadId))}
              />
            )}

            {activeTab === 'dashboard' && (
              <DashboardTab 
                leads={leads}
                setEditingLead={setEditingLead}
              />
            )}
            
            {/* Management Dashboard now standalone route from the sub-menu */}
            {activeTab === 'management-dashboard' && (
              <ManagementDashboardTab user={user} />
            )}

            {activeTab === 'manual' && <ManualTab />}

            {activeTab === 'licenses' && <LicensesTab currentUser={user} addToast={addToast} defaultSubTab="licenses" />}
            {activeTab === 'announcements' && <LicensesTab currentUser={user} addToast={addToast} defaultSubTab="announcements" />}
            {activeTab === 'bu-rules' && <BURulesTab currentUser={user} />}
            {activeTab === 'zalo-sandbox' && (
              <ZaloSandboxTab 
                setEditingLead={setEditingLead}
                handleConvertLead={handleConvertLead}
                leads={leads}
                users={users}
                tours={tourTemplates}
                currentUser={user}
                bus={bus}
                fetchLeads={fetchLeads}
              />
            )}

            {activeTab === 'zalo-ai-settings' && (
              <ZaloAISettingsTab 
                currentUser={user}
                addToast={addToast}
              />
            )}

            {activeTab === 'leads' && (
              <LeadsTab 
                currentUser={user}
                leads={leads}
                filteredLeads={filteredLeads}
                leadFilters={leadFilters}
                setLeadFilters={setLeadFilters}
                setShowAddLeadModal={setShowAddLeadModal}
                setEditingLead={setEditingLead}
                handleDeleteLead={handleDeleteLead}
                users={users}
                getSourceIcon={getSourceIcon}
                handleQuickUpdate={handleQuickUpdate}
                hoveredNote={hoveredNote}
                setHoveredNote={setHoveredNote}
                LEAD_STATUSES={LEAD_STATUSES}
                LEAD_SOURCES={LEAD_SOURCES}
                LEAD_CLASSIFICATIONS={LEAD_CLASSIFICATIONS}
                tours={tourTemplates}
                bus={bus}
                fetchLeads={fetchLeads}
                handleConvertLead={(leadId) => setLeadToConvert(leads.find(l => l.id === leadId))}
                navigateToInbox={handleOpenInboxDrawer}
                openZaloDrawer={handleOpenZaloDrawer}
              />
            )}
            {activeTab === 'leads-dashboard' && (
              <LeadsDashboardTab 
                setEditingLead={setEditingLead}
              />
            )}
            {activeTab === 'staff-performance' && (
              <StaffPerformanceTab bus={bus} />
            )}

            {activeTab === 'marketing-ads' && (
              <MarketingAdsTab addToast={addToast} currentUser={user} bus={bus.filter(b => b.is_active !== false)} />
            )}

            {activeTab === 'marketing-budget-plan' && (
              <MarketingBudgetPlanTab addToast={addToast} currentUser={user} bus={bus.filter(b => b.is_active !== false)} />
            )}

            {activeTab === 'marketing-google-ads' && (
              <GoogleAdsTab addToast={addToast} currentUser={user} />
            )}

        {activeTab === 'inbox' && (
          <InboxTab 
            setEditingLead={setEditingLead}
            handleConvertLead={handleConvertLead}
            leads={leads}
            users={users}
            tours={tourTemplates}
            currentUser={user}
            bus={bus}
            initialPsid={inboxPsid}
            clearInitialPsid={() => setInboxPsid(null)}
            onGoBack={() => {
              setInboxPsid(null);
              navigate('/leads');
              setActiveTab('leads');
            }}
          />
        )}



        {activeTab === 'tours' && (
          <ToursTab 
            tourTemplates={tourTemplates}
            tourFilters={tourFilters}
            setTourFilters={setTourFilters}
            setShowAddTemplateModal={setShowAddTemplateModal}
            setEditingTemplate={setEditingTemplate}
            handleDeleteTour={handleDeleteTour}
            handleUpdateTemplate={handleUpdateTemplate}
            bus={bus}
            currentUser={user}
            canCreate={checkPerm('tours', 'create')}
            canEdit={checkPerm('tours', 'edit')}
            canDelete={checkPerm('tours', 'delete')}
          />
        )}

        {activeTab === 'op-tours' && (
          <OpToursTab currentUser={user} />
        )}

        {activeTab === 'ceo-departures-dashboard' && (
          <CEODepartureDashboardTab currentUser={user} />
        )}

        {activeTab === 'vouchers' && (
          <PaymentVouchersTab currentUser={user} />
        )}

        {(activeTab === 'notification-center' && checkView('leads')) && (
          <GlobalChatTab 
            users={users} 
            tours={tourTemplates} 
            leads={leads}
            currentUser={user}
            bus={bus}
            setEditingLead={setEditingLead}
            navigateToInbox={handleOpenInboxDrawer} 
            openZaloDrawer={handleOpenZaloDrawer}
            fetchLeads={fetchLeads}
            handleConvertLead={(leadId) => {
              setLeadToConvert(leads.find(l => l.id === leadId));
            }}
          />
        )}



        {activeTab === 'dispatch-schedule' && (
          <DispatchScheduleTab bus={bus} users={users} />
        )}

        {activeTab === 'travel-support' && (
          <TravelSupportTab users={users} currentUser={user} checkPerm={checkPerm} />
        )}

        {activeTab === 'leaves' && (
          <LeaveRequestsTab users={users} currentUser={user} checkPerm={checkPerm} />
        )}
        {activeTab === 'meeting-rooms' && (
          <RoomBookingTab currentUser={user} />
        )}

        {activeTab === 'departures' && pathParts[1] === 'view' && pathParts[2] && (
          <ViewDeparturePage departureId={pathParts[2]} handleOpenCustomer={handleOpenCustomer} guides={guides} handleEditDeparture={handleEditDeparture} />
        )}

        {activeTab === 'departures' && (!pathParts[1]) && (
          <DeparturesTab 
            tourDepartures={tourDepartures}
            tourFilters={tourFilters}
            setTourFilters={setTourFilters}
            setShowAddDepartureModal={setShowAddDepartureModal}
            handleEditDeparture={handleEditDeparture}
            handleDeleteDeparture={handleDeleteDeparture}
            handleDuplicateDeparture={handleDuplicateDeparture}
            handleUpdateDeparture={handleUpdateDeparture}
            handleViewDeparture={handleViewDeparture}
            handleViewBookingsForDeparture={handleViewBookingsForDeparture}
            guides={guides}
            currentUser={user}
          />
        )}

            {activeTab === 'guides' && guideActiveTab !== 'reviews' && (
              <GuidesTab 
                guides={guides}
                guideFilters={guideFilters}
                setGuideFilters={setGuideFilters}
                guideActiveTab={guideActiveTab}
                setGuideActiveTab={setGuideActiveTab}
                fetchGuideTimeline={fetchGuideTimeline}
                setShowAddGuideModal={setShowAddGuideModal}
                handleOpenAddGuide={handleOpenAddGuide}
                setEditingGuide={setEditingGuide}
                handleEditGuide={handleEditGuide}
                handleDeleteGuide={handleDeleteGuide}
                guideTimeFilter={guideTimeFilter}
                setGuideTimeFilter={setGuideTimeFilter}
                guideTimelineData={guideTimelineData}
                guideStats={guideStats}
                fetchGuideStats={fetchGuideStats}
                hoveredNote={hoveredNote}
                setHoveredNote={setHoveredNote}
                tourDepartures={tourDepartures}
                handleEditDeparture={handleEditDeparture}
              />
            )}

        {activeTab === 'bookings' && (
          <BookingsTab 
            bookings={bookings}
            bookingFilters={bookingFilters}
            setBookingFilters={setBookingFilters}
            bookingCurrentPage={bookingCurrentPage}
            setBookingCurrentPage={setBookingCurrentPage}
            bookingTotalPages={bookingTotalPages}
            setShowAddBookingModal={setShowAddBookingModal}
            handleDeleteBooking={handleDeleteBooking}
            handleEditBooking={handleEditBooking}
          />
        )}

        {activeTab === 'costings' && (
          <CostingsTab user={user} />
        )}

        {activeTab === 'reminders' && (
          <RemindersTab handleViewDeparture={handleViewDeparture} />
        )}

        {activeTab === 'customers' && customerActiveTab === 'analytics' && (
          <CustomerAnalyticsDashboard 
            currentUser={user}
            onViewCustomer={(id) => {
              navigate(`/customers?view=${id}`);
              setCustomerActiveTab('list');
            }}
            onViewLead={(id) => {
              navigate(`/leads?edit=${id}`);
              setActiveTab('leads');
            }}
            onViewBooking={(id) => {
              navigate(`/bookings?edit=${id}`);
              setActiveTab('bookings');
            }}
          />
        )}

        {activeTab === 'customers' && customerActiveTab !== 'analytics' && customerActiveTab !== 'cskh-board' && customerActiveTab !== 'cskh-todo' && customerActiveTab !== 'cskh-search' && customerActiveTab !== 'cskh-rules' && customerActiveTab !== 'customer-reviews' && (
          <CustomersTab 
            currentUser={user}
            customers={customers}
            customerFilters={customerFilters}
            setCustomerFilters={setCustomerFilters}
            setShowAddCustomerModal={setShowAddCustomerModal}
            setEditingCustomer={handleEditCustomer}
            handleDeleteCustomer={handleDeleteCustomer}
            users={users}
            customerActiveTab={customerActiveTab}
            setCustomerActiveTab={setCustomerActiveTab}
          />
        )}

        {activeTab === 'customers' && customerActiveTab === 'cskh-board' && (
          <CskhBoardTab users={users} />
        )}

        {activeTab === 'customers' && customerActiveTab === 'cskh-todo' && (
          <CskhTodoTab users={users} customers={customers} />
        )}

        {activeTab === 'customers' && customerActiveTab === 'cskh-search' && (
          <CskhSearchTab users={users} tourTemplates={tourTemplates} />
        )}

        {activeTab === 'customers' && customerActiveTab === 'cskh-rules' && ['admin', 'manager'].includes(user?.role) && (
          <CskhRulesTab />
        )}

        {activeTab === 'guides' && guideActiveTab === 'reviews' && (
          <CustomerReviewsTab />
        )}

        {activeTab === 'users' && (checkPerm('users', 'view') || user?.role === 'admin' || user?.role === 'manager') && (
          <UsersTab 
            users={users}
            roles={roles}
            currentUser={user}
            checkPerm={checkPerm}
            onAddUser={() => setShowAddUserModal(true)}
            onEditUser={(u) => setEditingUserAccount(u)}
            onChangePassword={(u) => setUserToChangePassword(u)}
            onDeleteUser={handleDeleteUser}
            onEditRolePerms={() => setShowRolePermModal(true)}
            onEditUserPerms={(u) => setUserToEditPerms(u)}
          />
        )}

        {activeTab === 'staff-calendar' && (
          <div className="content-area">
            <div className="content-header">
              <h1>Lịch Sinh nhật & Kỷ niệm Nhân sự</h1>
            </div>
            <StaffCalendarView users={users} />
          </div>
        )}

        {activeTab === 'teams' && user?.role === 'admin' && (
          <TeamsTab addToast={addToast} users={users} />
        )}

        {activeTab === 'my-profile' && (
          <MyProfileTab currentUser={user} addToast={addToast} onUpdateUser={handleUpdateGlobalUser} />
        )}

        {activeTab === 'team-directory' && (
          <TeamDirectoryTab users={users} />
        )}

        {activeTab === 'org-chart' && (
          <OrgChartTab currentUser={user} addToast={addToast} />
        )}
        {activeTab === 'workflow' && (
          <WorkflowTab />
        )}

        {activeTab === 'settings' && (
          <SettingsTab 
            metaSettings={metaSettings}
            setMetaSettings={setMetaSettings}
            handleUpdateSettings={handleUpdateSettings}
            handleTestMeta={handleTestMeta}
            bus={bus}
            onUpdateBU={handleUpdateBU}
            onReorderBUs={handleReorderBUs}
            onCreateBU={handleCreateBU}
          />
        )}
        
        {activeTab === 'market-settings' && (
          <MarketSettingsTab checkPerm={checkPerm} />
        )}

        {activeTab === 'media-settings' && (
          <MediaSettingsTab addToast={addToast} />
        )}

        {activeTab === 'audit-logs' && (
          <AuditLogTab />
        )}



        {activeTab === 'notification-dashboard' && (
          <NotificationDashboardTab user={user} addToast={addToast} />
        )}

        {activeTab === 'email-groups' && (
          <EmailGroupsTab user={user} addToast={addToast} />
        )}

        {activeTab === 'email-rules' && (
          <EmailRulesTab user={user} addToast={addToast} />
        )}

        {activeTab === 'bus' && (
          <SettingsTab 
            metaSettings={{}} 
            bus={bus}
            onUpdateBU={handleUpdateBU}
            onReorderBUs={handleReorderBUs}
            onCreateBU={handleCreateBU}
            onlyBU={true}
          />
        )}

        {activeTab === 'hotels' && (
          <HotelsTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteHotel={(id) => setHotelToDelete(id)} />
        )}
        {activeTab === 'visas' && (
          <VisasTab currentUser={user} checkPerm={checkPerm} addToast={addToast} />
        )}
        {activeTab === 'visa-form-templates' && (
          <VisaFormTemplatesPage />
        )}
        {activeTab === 'visa-products' && isVisaProductViewer && (
          <VisaTemplatesTab currentUser={user} checkPerm={checkPerm} addToast={addToast} />
        )}
        {activeTab === 'restaurants' && (
          <RestaurantsTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteRestaurant={(id) => setRestaurantToDelete(id)} />
        )}
        {activeTab === 'transports' && (
          <TransportsTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteTransport={(id) => setTransportToDelete(id)} />
        )}
        {activeTab === 'tickets' && (
          <TicketsTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteTicket={(id) => setTicketToDelete(id)} />
        )}
        {activeTab === 'airlines' && (
          <AirlinesTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteAirline={(id) => setAirlineToDelete(id)} />
        )}
        {activeTab === 'landtours' && (
          <LandtoursTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteLandtour={(id) => setLandtourToDelete(id)} />
        )}
        {activeTab === 'insurances' && (
          <InsurancesTab currentUser={user} checkPerm={checkPerm} addToast={addToast} handleDeleteInsurance={(id) => setInsuranceToDelete(id)} />
        )}
        {activeTab === 'visa-providers' && (
          <VisaProvidersTab currentUser={user} checkPerm={checkPerm} checkView={checkView} addToast={addToast} setVisaProviderToDelete={(id) => setVisaProviderToDelete(id)} />
        )}

        {/* ═══ Tour Đoàn Tab Rendering ═══ */}
        {activeTab === 'b2b-companies' && (
          b2bActiveTab === 'calendar' ? 
            <GroupLeadersTab currentUser={user} addToast={addToast} users={users} activeView="calendar" /> :
            <B2BCompaniesTab currentUser={user} addToast={addToast} users={users} handleDeleteCompany={(id) => setB2bCompanyToDelete(id)} />
        )}
        {activeTab === 'group-leaders' && (
          <GroupLeadersTab currentUser={user} addToast={addToast} users={users} handleDeleteLeader={(id) => setGroupLeaderToDelete(id)} />
        )}
        {activeTab === 'group-mice-leads' && (
          <MiceLeadsTab currentUser={{...user, myPermissions}} addToast={addToast} users={users} />
        )}
        {activeTab === 'group-dashboard' && checkPerm('group_projects', 'view_dashboard') && (
          <GroupDashboardTab />
        )}
        {activeTab === 'group-projects' && (
          <GroupProjectsTab currentUser={{...user, myPermissions}} addToast={addToast} users={users} handleDeleteProject={(id) => setGroupProjectToDelete(id)} canViewProfit={checkPerm('group_projects', 'view_profit')} />
        )}
        {activeTab === 'passport-ocr' && (
          <PassportBulkScanner />
        )}

      </Suspense>
    )}
  </main>

      <AddLeadModal 
        showAddLeadModal={showAddLeadModal}
        setShowAddLeadModal={setShowAddLeadModal}
        handleAddLead={handleAddLead}
        newLead={newLead}
        setNewLead={setNewLead}
        LEAD_SOURCES={LEAD_SOURCES}
        LEAD_CLASSIFICATIONS={LEAD_CLASSIFICATIONS}
        LEAD_STATUSES={LEAD_STATUSES}
        tours={tourTemplates}
        users={users}
        bus={bus.filter(b => b.is_active !== false)}
      />

      {showLeaveModal && (
        <LeaveRequestModal 
            currentUser={user} 
            users={users} 
            onClose={() => setShowLeaveModal(false)} 
            onSuccess={() => { 
                navigate('/staff-calendar');
                setActiveTab('staff-calendar');
            }} 
        />
      )}

      <AddBookingModal
        show={showAddBookingModal}
        bookingToEdit={bookingToEdit}
        onClose={() => {
          setShowAddBookingModal(false);
          setBookingToEdit(null);
        }}
        onSave={async (bookingData) => {
          try {
            let finalCustomerId = bookingData.customer_id;
            
            if (bookingData.is_new_customer) {
              const custRes = await axios.post('/api/customers', {
                name: bookingData.new_customer_info.name,
                phone: bookingData.new_customer_info.phone,
                preferred_contact: 'Zalo',
                role: 'booker'
              }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              finalCustomerId = custRes.data.id;
            }

            if (bookingToEdit) {
              await axios.put(`/api/bookings/${bookingToEdit.id}`, {
                ...bookingData,
                customer_id: finalCustomerId
              }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              addToastGlobal('Cập nhật đơn hàng thành công!', setToasts);
            } else {
              await axios.post('/api/bookings', {
                ...bookingData,
                customer_id: finalCustomerId
              }, { 
                headers: { 
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  'Idempotency-Key': typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `idemp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                } 
              });
              addToastGlobal('Lưu đơn hàng thành công!', setToasts);
            }
            
            const updatedBookings = await axios.get('/api/bookings', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            setBookings(updatedBookings.data);
            setShowAddBookingModal(false);
            setBookingToEdit(null);
          } catch (err) {
            console.error(err);
            alert('Lỗi lưu đơn hàng: ' + (err.response?.data?.message || err.message));
          }
        }}
        customers={customers}
        departures={tourDepartures}
      />

      <AddCustomerModal 
        showAddCustomerModal={showAddCustomerModal}
        setShowAddCustomerModal={setShowAddCustomerModal}
        handleAddCustomer={handleAddCustomer}
        newCustomer={newCustomer}
        setNewCustomer={setNewCustomer}
        CITY_OPTIONS={CITY_OPTIONS}
        CUSTOMER_ROLES={CUSTOMER_ROLES}
        CUSTOMER_SEGMENTS={CUSTOMER_SEGMENTS}
        users={users}
      />

      <EditCustomerModal 
        editingCustomer={editingCustomer}
        setEditingCustomer={setEditingCustomer}
        handleUpdateCustomer={handleUpdateCustomer}
        CITY_OPTIONS={CITY_OPTIONS}
        CUSTOMER_ROLES={CUSTOMER_ROLES}
        CUSTOMER_SEGMENTS={CUSTOMER_SEGMENTS}
        newCustomerNote={newCustomerNote}
        setNewCustomerNote={setNewCustomerNote}
        handleAddCustomerNote={handleAddCustomerNote}
        users={users}
        currentUser={user}
      />

      <LeadNotesModal 
        selectedLeadForNotes={selectedLeadForNotes}
        setSelectedLeadForNotes={setSelectedLeadForNotes}
        handleAddNote={handleAddNote}
        newNote={newNote}
        setNewNote={setNewNote}
        leadNotes={leadNotes}
      />

      <AddTemplateModal 
        showAddTemplateModal={showAddTemplateModal}
        setShowAddTemplateModal={setShowAddTemplateModal}
        handleAddTemplate={handleAddTemplate}
        newTemplate={newTemplate}
        setNewTemplate={setNewTemplate}
        bus={bus.filter(b => b.is_active !== false)}
      />

      <EditTemplateModal 
        template={editingTemplate}
        onClose={() => setEditingTemplate(null)}
        onUpdate={handleUpdateTemplate}
        bus={bus.filter(b => b.is_active !== false || b.id === editingTemplate?.bu_group)}
        tourDepartures={tourDepartures}
      />

      <AddDepartureModal 
        showAddDepartureModal={showAddDepartureModal}
        setShowAddDepartureModal={setShowAddDepartureModal}
        handleAddDeparture={handleAddDeparture}
        newDeparture={newDeparture}
        setNewDeparture={setNewDeparture}
        tourTemplates={tourTemplates}
        guides={guides}
      />

      <EditDepartureModal 
        showEditDepartureModal={showEditDepartureModal}
        setShowEditDepartureModal={setShowEditDepartureModal}
        handleUpdateDeparture={handleUpdateDeparture}
        editingDeparture={editingDeparture}
        setEditingDeparture={setEditingDeparture}
        tourTemplates={tourTemplates}
        guides={guides}
        handleOpenCustomer={handleOpenCustomer}
      />

      <GuideModal 
        showAddGuideModal={showAddGuideModal}
        setShowAddGuideModal={setShowAddGuideModal}
        editingGuide={editingGuide}
        setEditingGuide={setEditingGuide}
        handleUpdateGuide={handleUpdateGuide}
        handleAddGuide={handleAddGuide}
        newGuide={newGuide}
        setNewGuide={setNewGuide}
        guideTimelineData={guideTimelineData}
        tourDepartures={tourDepartures}
        handleEditDeparture={handleEditDeparture}
      />

      <AddUserModal 
        show={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSave={handleAddUser}
        roles={roles}
      />

      <EditUserModal 
        user={editingUserAccount}
        onClose={() => setEditingUserAccount(null)}
        onSave={handleUpdateUser}
        roles={roles}
      />

      <RolePermissionModal 
        open={showRolePermModal} 
        onClose={() => setShowRolePermModal(false)}
        addToast={addToast}
      />
      
      <UserPermissionOverrideModal 
        open={!!userToEditPerms} 
        user={userToEditPerms}
        onClose={() => setUserToEditPerms(null)}
        addToast={addToast}
      />

      <ChangePasswordModal 
        user={showChangeMyPassword ? user : userToChangePassword}
        onClose={() => {
          setUserToChangePassword(null);
          setShowChangeMyPassword(false);
        }}
        onSave={async (id, newPassword) => {
          if (showChangeMyPassword) {
            try {
              const token = localStorage.getItem('token');
              await axios.put(`/api/users/${id}/password`, { newPassword }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              addToast('Đã đổi mật khẩu cá nhân thành công.');
              setShowChangeMyPassword(false);
            } catch (err) {
              addToast(err.response?.data?.message || 'Lỗi khi đổi mật khẩu');
            }
          } else {
            handleChangePasswordAdmin(id, newPassword);
          }
        }}
      />

      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className="toast" style={t.type === 'error' ? { background: '#ef4444', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } : {}}>
             <span style={{marginRight: '12px'}}>{t.message}</span>
             {t.type === 'error' && (
               <button onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))} style={{background:'transparent', border:'none', color:'white', cursor:'pointer', fontSize:'16px', fontWeight:'bold', padding:'0 4px', lineHeight: 1}}>×</button>
             )}
          </div>
        ))}
      </div>
    </div>
    );
  };

  return (
    <>
      <Routes>
      <Route path="/huong-dan-erp/tao-nghi-phep" element={<HuongDanTaoNghiPhepPage />} />
      <Route path="/huong-dan-erp/dat-phong-hop" element={<HuongDanDatPhongHopPage />} />
      <Route path="/huong-dan-erp" element={<HuongDanErpHub />} />
      <Route path="/huong-dan-erp/*" element={<HuongDanErpHub />} />
      <Route path="/tai-lieu/manager" element={isLoggedIn ? <RagDocsManager /> : <Navigate to="/login" />} />
      <Route path="/tai-lieu" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/tai-lieu/sop-astro-tour" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/tai-lieu/rule-meta-ads" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/tai-lieu/blueprint-meta-ads" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />

      <Route path="/tai-lieu/:subtab/:id/*" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/tai-lieu/*" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/hdv" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/hdv/*" element={isLoggedIn ? <DocumentsPage /> : <Navigate to="/login" />} />
      <Route path="/cam-nang-thuong-hieu/logo" element={isLoggedIn ? <BrandLayout><BrandLogoPage /></BrandLayout> : <Navigate to="/login" />} />
      <Route path="/cam-nang-thuong-hieu/mau-sac" element={isLoggedIn ? <BrandLayout><BrandColorPage /></BrandLayout> : <Navigate to="/login" />} />
      <Route path="/cam-nang-thuong-hieu/phong-chu" element={isLoggedIn ? <BrandLayout><BrandTypographyPage /></BrandLayout> : <Navigate to="/login" />} />
      <Route path="/cam-nang-thuong-hieu/facebook" element={isLoggedIn ? <BrandLayout><BrandSocialPage /></BrandLayout> : <Navigate to="/login" />} />
      <Route path="/cam-nang-thuong-hieu" element={isLoggedIn ? <BrandLayout><BrandGuidelinePage /></BrandLayout> : <Navigate to="/login" />} />
      <Route path="/cam-nang-thuong-hieu/*" element={isLoggedIn ? <BrandLayout><BrandGuidelinePage /></BrandLayout> : <Navigate to="/login" />} />
      <Route path="/simple-list-share/lich_dai_ly" element={<AgencySharePage />} />
      <Route path="/receipt/:token" element={<PublicReceiptPage />} />
      <Route path="/service-confirm/:tourId/:bookingId" element={<ServiceContractViewer />} />
      <Route path="/visa-portal/:token" element={<PublicVisaPortalPage />} />
      <Route path="/thong-bao/:id" element={<PublicAnnouncementPage />} />
      <Route path="/thong-bao/code/:code" element={<PublicAnnouncementPage />} />
      <Route path="/announcements/:id" element={<PublicAnnouncementPage />} />
      <Route path="/van-ban/:id" element={<PublicAnnouncementPage />} />
      <Route path="/zns-demo" element={isLoggedIn ? <ZnsDemoTab /> : <Navigate to="/login" />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/chinh-sach-bao-mat" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/deletion" element={<DataDeletion />} />
      <Route 
        path="/login" 
        element={isLoggedIn ? <Navigate to={`/${activeTab}`} replace /> : (
          <div className="login-page-wrapper">
            <div className="login-glass-card shadow-2xl">
              <div className="login-brand" style={{ display: 'flex', justifyContent: 'center' }}>
                <img src="/logo.png" alt="FIT TOUR" style={{ height: '80px', marginBottom: '1rem', objectFit: 'contain' }} />
              </div>

              <h2>Đăng nhập hệ thống</h2>
              
              {error && (
                <div className="error-msg animate-shake mb-6 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-sm font-semibold flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin}>
                <div className="login-form-group">
                  <label>Tên đăng nhập</label>
                  <div className="login-input-wrapper">
                    <User size={18} />
                    <input 
                      type="text" 
                      className="login-input"
                      placeholder="Nhập username..."
                      required 
                      value={loginData.username} 
                      onChange={(e) => setLoginData({...loginData, username: e.target.value})} 
                    />
                  </div>
                </div>

                <div className="login-form-group">
                  <label>Mật khẩu</label>
                  <div className="login-input-wrapper" style={{ position: 'relative' }}>
                    <Lock size={18} />
                    <input 
                      type={showPassword ? "text" : "password"} 
                      className="login-input"
                      placeholder="••••••••"
                      required 
                      value={loginData.password} 
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})} 
                      style={{ paddingRight: '40px' }}
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ 
                        position: 'absolute', 
                        right: '10px', 
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent', 
                        border: 'none', 
                        cursor: 'pointer', 
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="login-submit-btn">
                  Đăng nhập
                </button>
              </form>

              <div style={{ display: 'flex', alignItems: 'center', margin: '1.25rem 0' }}>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
                <span style={{ padding: '0 10px', color: '#6b7280', fontSize: '0.85rem' }}>Hoặc</span>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              </div>
              
              <a 
                href={window.location.hostname === 'localhost' ? 'http://localhost:5001/api/auth/google' : 'https://erp.fittour.vn/api/auth/google'}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  width: '100%', padding: '0.75rem', background: 'white', border: '1px solid #d1d5db',
                  borderRadius: '8px', color: '#374151', fontWeight: '600', textDecoration: 'none',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s',
                  marginBottom: '1.5rem'
                }}
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: '18px', height: '18px' }} />
                Đăng nhập bằng Google
              </a>

              <div className="login-footer-links">
                <button type="button" onClick={() => navigate('/privacy')} className="login-footer-link">
                  Chính sách bảo mật
                </button>
                <div className="login-footer-sub">
                  <button type="button" onClick={() => navigate('/terms')} className="login-footer-link">
                    Điều khoản dịch vụ
                  </button>
                  <button type="button" className="login-footer-link" onClick={() => navigate('/deletion')}>
                    Xóa dữ liệu
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} 
      />
      <Route path="/admin-trip" element={<AdminTripDashboard />} />
      <Route path="/admin-trip/*" element={<AdminTripDashboard />} />
      
      <Route 
        path="/:tab" 
        element={isLoggedIn ? renderDashboard() : <Navigate to="/login" />} 
      />
      <Route 
        path="/:tab/:subtab" 
        element={isLoggedIn ? renderDashboard() : <Navigate to="/login" />} 
      />
      <Route 
        path="/:tab/:subtab/:id" 
        element={isLoggedIn ? renderDashboard() : <Navigate to="/login" />} 
      />
      <Route 
        path="/" 
        element={isLoggedIn ? <Navigate to="/notification-center" replace /> : <Navigate to="/login" />}
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    {/* MODAL XÁC NHẬN CHUYỂN ĐỔI LEAD SANG KHÁCH HÀNG */}
    {leadToConvert && (
      <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setLeadToConvert(null)}>
        <div className="modal-content animate-slide-up" style={{ maxWidth: '400px', textAlign: 'center', padding: '2.5rem' }} onClick={e => e.stopPropagation()}>
          <div style={{ width: '60px', height: '60px', background: '#d1fae5', color: '#10b981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <UserPlus size={32} />
          </div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem', color: '#111827' }}>Chuyển đổi thành Khách Hàng?</h3>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', lineHeight: '1.5', marginBottom: '2rem' }}>
            Bạn đang chuyển <strong>{leadToConvert.name}</strong> sang dạng Khách Hàng chính thức. Trạng thái tư vấn của Lead này sẽ được tự động đổi sang "Chốt đơn".
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              className="btn-pro-cancel" 
              style={{ flex: 1, border: '1px solid #e5e7eb', background: 'white' }}
              disabled={loading}
              onClick={() => setLeadToConvert(null)}
            >HỦY BỎ</button>
            <button 
              className="btn-pro-save" 
              style={{ flex: 1, background: '#10b981', opacity: loading ? 0.7 : 1 }}
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                await handleConvertLead(leadToConvert.id);
                setLeadToConvert(null);
                setLoading(false);
              }}
            >{loading ? 'ĐANG CHUYỂN...' : 'CHUYỂN ĐỔI'}</button>
          </div>
        </div>
      </div>
    )}

    {/* MODAL XÁC NHẬN XÓA (CUSTOM) */}
    {(leadToDelete || customerToDelete || tourToDelete || departureToDelete || bookingToDelete || hotelToDelete || restaurantToDelete || transportToDelete || ticketToDelete || airlineToDelete || landtourToDelete || insuranceToDelete || b2bCompanyToDelete || groupLeaderToDelete || groupProjectToDelete || visaProviderToDelete) && (
      <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => {
        setLeadToDelete(null);
        setCustomerToDelete(null);
        setTourToDelete(null);
        setDepartureToDelete(null);
        setBookingToDelete(null);
        setHotelToDelete(null);
        setRestaurantToDelete(null);
        setTransportToDelete(null);
        setTicketToDelete(null);
        setAirlineToDelete(null);
        setLandtourToDelete(null);
        setInsuranceToDelete(null);
        setB2bCompanyToDelete(null);
        setGroupLeaderToDelete(null);
        setGroupProjectToDelete(null);
        setVisaProviderToDelete(null);
      }}>
        <div className="modal-content animate-slide-up" style={{ maxWidth: '400px', textAlign: 'center', padding: '2.5rem' }} onClick={e => e.stopPropagation()}>
          <div style={{ width: '60px', height: '60px', background: '#fee2e2', color: '#ef4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <AlertTriangle size={32} />
          </div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem', color: '#111827' }}>Xác nhận xóa dữ liệu?</h3>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', lineHeight: '1.5', marginBottom: '2rem' }}>
            Hành động này không thể hoàn tác. Bạn có chắc chắn muốn xóa bản ghi này khỏi hệ thống?
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              className="btn-pro-cancel" 
              style={{ flex: 1, border: '1px solid #e5e7eb', background: 'white' }}
              disabled={loading}
              onClick={() => {
                setLeadToDelete(null);
                setCustomerToDelete(null);
                setTourToDelete(null);
                setDepartureToDelete(null);
                setBookingToDelete(null);
                setHotelToDelete(null);
                setRestaurantToDelete(null);
                setTransportToDelete(null);
                setTicketToDelete(null);
                setAirlineToDelete(null);
                setLandtourToDelete(null);
                setInsuranceToDelete(null);
                setB2bCompanyToDelete(null);
                setGroupLeaderToDelete(null);
                setGroupProjectToDelete(null);
                setVisaProviderToDelete(null);
              }}
            >HỦY BỎ</button>
            <button 
              className="btn-pro-save" 
              style={{ flex: 1, background: '#ef4444', opacity: loading ? 0.7 : 1 }}
              disabled={loading}
              onClick={() => {
                if (leadToDelete) confirmDeleteLead();
                if (customerToDelete) confirmDeleteCustomer();
                if (tourToDelete) confirmDeleteTour();
                if (departureToDelete) confirmDeleteDeparture();
                if (bookingToDelete) confirmDeleteBooking();
                if (hotelToDelete) confirmDeleteHotel();
                if (restaurantToDelete) confirmDeleteRestaurant();
                if (transportToDelete) confirmDeleteTransport();
                if (ticketToDelete) confirmDeleteTicket();
                if (airlineToDelete) confirmDeleteAirline();
                if (landtourToDelete) confirmDeleteLandtour();
                if (insuranceToDelete) confirmDeleteInsurance();
                if (b2bCompanyToDelete) confirmDeleteB2bCompany();
                if (groupLeaderToDelete) confirmDeleteGroupLeader();
                if (groupProjectToDelete) confirmDeleteGroupProject();
                if (visaProviderToDelete) confirmDeleteVisaProvider();
                }}
            >{loading ? 'ĐANG XÓA...' : 'XÓA THỰC SỰ'}</button>
          </div>
        </div>
      </div>
    )}

    {/* INBOX DRAWER OVERLAY (GLOBAL) */}
    {isInboxDrawerOpen && (
        <div className="inbox-drawer-overlay">
            <div className="inbox-drawer-content">
                <Suspense fallback={<div className="flex items-center justify-center h-full p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div></div>}>
                    <InboxTab 
                        initialPsid={drawerInboxPsid} 
                        clearInitialPsid={() => setDrawerInboxPsid(null)}
                        onGoBack={() => setIsInboxDrawerOpen(false)}
                        leads={leads}
                        users={users}
                        tours={tourTemplates}
                        handleConvertLead={handleConvertLead}
                        currentUser={user}
                        bus={bus}
                        setEditingLead={setEditingLead}
                        goBackText="Đóng Inbox"
                    />
                </Suspense>
            </div>
        </div>
    )}

    {/* ZALO DRAWER OVERLAY (GLOBAL) */}
    {isZaloDrawerOpen && (
        <div className="inbox-drawer-overlay">
            <div className="inbox-drawer-content">
                <Suspense fallback={<div className="flex items-center justify-center h-full p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div></div>}>
                    <ZaloChatDrawer 
                        initialZaloUid={drawerZaloUid} 
                        onClose={() => setIsZaloDrawerOpen(false)}
                        leads={leads}
                        currentUser={user}
                    />
                </Suspense>
            </div>
        </div>
    )}
  </>
);
}

function App() {
  return (
    <Router>
      <Toaster position="bottom-right" toastOptions={{ duration: 4000 }} />
      <AppContent />
    </Router>
  );
}

export default App;
